import invert from 'lodash/invert'
import { CollateralTypes, LOAN_TYPES, UCC_SEARCH_LABEL } from '../constants/client'
import { FilterFieldType } from '../constants/filters/types'
import { enumNarrow } from '../helpers/enumGuard'
import { IActivity } from './activity'
import { IBankAccount } from './bankAccount'
import { IFile } from './box'
import { FieldType, IAmendment, IClientInfo, OveradvanceStatus } from './client'
import { IEntityInfo } from './entityInfo'
import { AmendmentAnalysisStatus, IOpenAmendmentAnalysis } from './generalAnalysis'
import { IFee } from './loanServicing'
import { INote } from './notes'
import { Feedback, IFlagFeedback, IOPSReporting } from './prospects'
import { IRequiredReport } from './requiredReport'

export enum FundingRequestStatus {
  Requested = 'REQUESTED',
  InProgress = 'IN PROGRESS',
  Review = 'READY FOR REVIEW',
  Completed = 'COMPLETED',
  Sent = 'SENT',
}

export const FILE_ATTACHABLE_BORROWING_BASE_STATUSES = [
  FundingRequestStatus.Requested,
  FundingRequestStatus.InProgress,
  FundingRequestStatus.Review,
]

export enum PaymentStatus {
  Unpaid = 'Unpaid',
  PartiallyPaid = 'Partially Paid',
  Paid = 'Paid',
  ImpliedPaid = 'Implied Paid',
}

export const FUNDING_REQUEST_STATUS_COMPLETED = [
  FundingRequestStatus.Completed,
  FundingRequestStatus.Sent,
]

/**
 * True when a BBC is finalized: no further edits, recalculation, or deletion.
 *
 * Drives write guards on the server and disabled controls in the UI. Test BBCs stop at
 * `Completed` and never reach `Sent`, so both statuses count as finalized.
 *
 * This is NOT a read-source check. A Completed real BBC is locked for editing but still reads
 * the client's live collateral — use `checkIsHistorical` for that.
 */
export const checkIsFinalized = (status?: FundingRequestStatus | null): boolean =>
  !!status && FUNDING_REQUEST_STATUS_COMPLETED.includes(status)

/**
 * True when a BBC's collateral snapshot has been frozen, meaning reads should come from the
 * historical tables (`historical_borrowing_base_id = <bbc id>`) rather than the client's live
 * rows.
 *
 * This is NOT a lock check — see `checkIsFinalized`. The two are deliberately separate: for a
 * real BBC the snapshot is taken when it is Sent (see outgoingWire/Sent.ts), but it is locked
 * from editing one step earlier, at Completed. Keeping them distinct is what lets a Completed
 * BBC show live eligibility rules while still being read-only.
 *
 * Test BBCs are the exception, and the reason this takes the BBC rather than just its status:
 * a test BBC never reaches Sent (outgoingWire/Sent.ts skips it), so it takes its snapshot
 * inline at completion instead (bbc/Submit.ts, `shouldSaveTestData`). Keying on status alone
 * would point a completed test BBC at live rows its collateral no longer lives in.
 */
export const checkIsHistorical = (
  bbc?: { status?: FundingRequestStatus | null; isTest?: boolean | null } | null,
): boolean =>
  bbc?.status === FundingRequestStatus.Sent ||
  (!!bbc?.isTest && bbc?.status === FundingRequestStatus.Completed)

/** The `client_collateral` figures that reveal which collateral a BBC period was funded against. */
type CollateralBasisSnapshot = {
  totalGrossAr?: number | null
  totalReceivables?: number | null
  arAvailability?: number | null
  totalGrossInventory?: number | null
  totalInventory?: number | null
  eligibleInventoryCost?: number | null
  inventoryAvailabilityPostSubLimit?: number | null
}

const AR_BASIS_FIELDS = ['totalGrossAr', 'totalReceivables', 'arAvailability'] as const

const INVENTORY_BASIS_FIELDS = [
  'totalGrossInventory',
  'totalInventory',
  'eligibleInventoryCost',
  'inventoryAvailabilityPostSubLimit',
] as const

const hasCollateralBasis = (
  collateral: CollateralBasisSnapshot | null | undefined,
  fields: readonly (keyof CollateralBasisSnapshot)[],
): boolean => !!collateral && fields.some((field) => Math.abs(collateral[field] ?? 0) > 0)

/**
 * The collateral basis a BBC's own period was funded against, which is not always the client's
 * live `loanType`.
 *
 * Moving a client from "AR & Inventory" to "AR" rewrites nothing: every finalized BBC keeps the
 * inventory figures it was calculated with. Gating the UI on the live loanType therefore hides
 * numbers those BBCs still carry. The period's basis is read back out of its own
 * `client_collateral` snapshot instead, so a client that has flipped several times resolves
 * each period independently rather than from a single cutoff date.
 *
 * Only finalized BBCs widen. A snapshot carries inventory figures whenever the BBC has
 * `client_inventory` rows — ClientCollateralCalculate sums `totalGrossInventory` with no loanType
 * condition — so an in-progress BBC whose loan type was switched mid-cycle would otherwise widen
 * too, and BuildPDF/ExportBBC would hand the client an "AR & Inventory" document for a period
 * still being funded as AR.
 *
 * Widening only — the live loanType's own collateral is never dropped, so no section that renders
 * today can disappear.
 */
export const resolveBBCLoanType = (
  bbc?: {
    status?: FundingRequestStatus | null
    clientInfo?: { loanType?: string | null } | null
    clientCollateral?: CollateralBasisSnapshot | null
  } | null,
): string | null | undefined => {
  const liveLoanType = bbc?.clientInfo?.loanType

  if (!checkIsFinalized(bbc?.status)) {
    return liveLoanType
  }

  if (
    liveLoanType === LOAN_TYPES.ar &&
    hasCollateralBasis(bbc?.clientCollateral, INVENTORY_BASIS_FIELDS)
  ) {
    return LOAN_TYPES.arAndInventory
  }

  if (
    liveLoanType === LOAN_TYPES.inventory &&
    hasCollateralBasis(bbc?.clientCollateral, AR_BASIS_FIELDS)
  ) {
    return LOAN_TYPES.arAndInventory
  }

  return liveLoanType
}

export type ConsolidatedFinancialItem = {
  id: string
  field: string
  income: string
  account: string
  values: FinancialsEntry[]
  [key: string]: any
}

export const MAINTENANCE_COLUMNS = [
  'id',
  'client_name',
  'record_date',
  'as_of_date',
  'financials_type',
  'version',
  'dilution',
  'created_at',
  'updated_at',
  'deleted_at',
  'ongoing_reporting_id',
  'ops_reporting_id',
  'ops_reporting_flows_id',
  'file_id',
  'sheet_name',
  'dd_reporting_id',
  'dd_reporting_flows_id',
]

export interface FinancialsEntry {
  amount: number
  period: string
}

export enum FundingRequestAmountType {
  Specific = 'Specific',
  Max = 'Max',
  TBD = 'TBD',
}

export enum ClientInventoryTables {
  Historical = 'historical_client_inventory',
  Current = 'client_inventory',
}

export enum IneligibleReasons {
  SKUsAndSeason = 'Ineligible SKUs and Season',
  Locations = 'Ineligible Locations and In-Transit',
  SlowMoving = 'Slow Moving, Aged, Near/Past Expiration',
  OtherMiscReserves = 'Other Misc. Reserves',
}

export const INELIGIBLE_REASONS_LIST = Object.values(IneligibleReasons).map((value) => ({
  value,
  label: value,
}))

export const IneligibleReasonsMap: Record<string, IneligibleReasons> = {
  sku: IneligibleReasons.SKUsAndSeason,
  type: IneligibleReasons.SKUsAndSeason,
  season: IneligibleReasons.SKUsAndSeason,
  size: IneligibleReasons.SKUsAndSeason,
  flavor: IneligibleReasons.SKUsAndSeason,
  style: IneligibleReasons.SKUsAndSeason,
  color: IneligibleReasons.SKUsAndSeason,
  brandCategory: IneligibleReasons.SKUsAndSeason,
  industrySubcategory: IneligibleReasons.SKUsAndSeason,
  location: IneligibleReasons.Locations,
  daysSinceLanded: IneligibleReasons.SlowMoving,
  daysUntilExpiration: IneligibleReasons.SlowMoving,
  sales: IneligibleReasons.SlowMoving,
  weeksOfSupply: IneligibleReasons.SlowMoving,
  lotNumber: IneligibleReasons.OtherMiscReserves,
  onHand: IneligibleReasons.OtherMiscReserves,
  avgUnitCost: IneligibleReasons.OtherMiscReserves,
  totalValue: IneligibleReasons.OtherMiscReserves,
  landedDate: IneligibleReasons.OtherMiscReserves,
  expirationDate: IneligibleReasons.OtherMiscReserves,
  salesUnits: IneligibleReasons.OtherMiscReserves,
  custom_1: IneligibleReasons.OtherMiscReserves,
  custom_2: IneligibleReasons.OtherMiscReserves,
  custom_3: IneligibleReasons.OtherMiscReserves,
  custom_4: IneligibleReasons.OtherMiscReserves,
  custom_5: IneligibleReasons.OtherMiscReserves,
}

export const FUNDING_REQUEST_AMOUNT_TYPE_LABEL = {
  [FundingRequestAmountType.Specific]: 'Specific',
  [FundingRequestAmountType.Max]: 'Max Amount',
  [FundingRequestAmountType.TBD]: 'TBD',
}

export enum OngoingReportingStatus {
  NotStarted = 'Not Started',
  Uploaded = 'Uploaded',
  Process = 'Process',
  InReview = 'In Review',
  Verified = 'Verified',
  Archived = 'Archived',
}

// Set when Atlas pulled the report itself, or Arc filed it for someone; null for an upload.
export enum OngoingReportingSource {
  Codat = 'Codat',
  Arc = 'Arc',
}

export const REPORTING_STATUS_LABEL = {
  [OngoingReportingStatus.NotStarted]: 'Not Started',
  [OngoingReportingStatus.Process]: 'In Progress',
  [OngoingReportingStatus.InReview]: 'In Review',
  [OngoingReportingStatus.Verified]: 'Complete',
  [OngoingReportingStatus.Uploaded]: 'Uploaded',
  [OngoingReportingStatus.Archived]: 'Archived',
}

export enum OngoingReportingType {
  BalanceSheet = 'BalanceSheet',
  IncomeStatement = 'IncomeStatement',
  IncomeStatementProjections = 'IncomeStatementProjections',
  BalanceSheetProjections = 'BalanceSheetProjections',
  ARGeneralLedger = 'ARGeneralLedger',
  SalesBySKU = 'SalesBySKU',
  AR = 'ARAging',
  AP = 'APAging',
  Inventory = 'InventoryDetail',
  BankTransactions = 'BankTransactions',
  CapTable = 'CapTable',
  OrgChart = 'OrgChart',
  Summary = 'Summary',
  Miscellaneous = 'Miscellaneous',
  Uncategorized = 'Uncategorized',
  Insurance = 'Insurance',
  UCC = 'UCC',
  GS = 'GS',
}

/** Cast-free narrower + type guard for OngoingReportingType (see common/helpers/enumGuard). */
const ongoingReportingTypeNarrow = enumNarrow(OngoingReportingType)
export const asOngoingReportingType = ongoingReportingTypeNarrow.as
export const isOngoingReportingType = ongoingReportingTypeNarrow.is

export const ONGOING_REPORTING_TYPE_LABEL: Partial<Record<OngoingReportingType, string>> = {
  [OngoingReportingType.BalanceSheet]: 'Balance Sheet',
  [OngoingReportingType.IncomeStatement]: 'Income Statement',
  [OngoingReportingType.IncomeStatementProjections]: 'Income Statement (Projections)',
  [OngoingReportingType.BalanceSheetProjections]: 'Balance Sheet (Projections)',
  [OngoingReportingType.ARGeneralLedger]: 'AR General Ledger',
  [OngoingReportingType.SalesBySKU]: 'Sales by SKU',
  [OngoingReportingType.AR]: 'AR Aging',
  [OngoingReportingType.AP]: 'AP Aging',
  [OngoingReportingType.Inventory]: 'Inventory Detail',
  [OngoingReportingType.BankTransactions]: 'Bank Transactions',
  [OngoingReportingType.CapTable]: 'Cap Table',
  [OngoingReportingType.OrgChart]: 'Org Chart',
  [OngoingReportingType.Summary]: 'Summary',
  [OngoingReportingType.UCC]: UCC_SEARCH_LABEL,
  [OngoingReportingType.GS]: 'GS',
}

export enum TemplateTypes {
  ARDetail = 'arRaw',
  ARSummary = 'arRawSummary',
  APDetail = 'apRaw',
  APSummary = 'apRawSummary',
  InventoryDetail = 'inventory',
  IncomeStatement = 'incomeStatement',
  BalanceSheet = 'balanceSheet',
  ARGeneralLedger = 'arGeneralLedger',
  SalesBySKU = 'salesBySKU',
  SalesBySKUMonthlyHeader = 'salesBySKUMonthlyHeader',
  CapTable = 'capTable',
  BankStatement = 'bankStatement',
}

export interface IDrillDownData {
  data: IOngoingReportingRawMapping[]
  headers: string[]
  totals?: {
    totalItems: number
    totalMapped: number
    totalFields: number
  }
}

export interface IAccountData {
  id: string
  label: string
  values: number[]
}

export interface IMappingData {
  [field: string]: IAccountData[]
}

export enum ReportingPeriods {
  Daily = 'Daily',
  Weekly = 'Weekly',
  Monthly = 'Monthly',
  Quarterly = 'Quarterly',
  Annually = 'Annually',
}

/** Same values as MUI's DateView; declared here so the server build doesn't load MUI types. */
type CalendarView = 'year' | 'month' | 'day'

export const CALENDAR_VIEWS_MAP: Record<ReportingPeriods, CalendarView[]> = {
  [ReportingPeriods.Annually]: ['year'],
  [ReportingPeriods.Daily]: ['day'],
  [ReportingPeriods.Weekly]: ['day'],
  [ReportingPeriods.Monthly]: ['month', 'year'],
  [ReportingPeriods.Quarterly]: ['month', 'year'],
}

export enum ComparisonBase {
  NetRevenue = 'net_revenue',
  GrossRevenue = 'gross_revenue',
  Hide = 'hide',
}

export const COMPARISON_BASE_OPTIONS = [
  { value: ComparisonBase.NetRevenue, label: 'Net Revenue' },
  { value: ComparisonBase.GrossRevenue, label: 'Gross Revenue' },
  { value: ComparisonBase.Hide, label: 'Hide' },
]

export enum AnnuallyReportingPeriods {
  YTD = 'YTD',
  FY = 'FY',
}

export enum FinancialRowTypes {
  Header = 'Header',
  Total = 'Total',
  SubTotal = 'SubTotal',
  Normal = 'Normal',
}

export enum DisplayGrowthPeriods {
  Hide = 'Hide',
  Month = 'Month-over-month',
  Quarter = 'Quarter-over-quarter',
  Year = 'Year-over-year',
  ActualsProjections = 'Actuals/Projections',
  StressBase = 'Stress/Base',
}

export const REPORTING_PERIOD_OPTIONS = Object.values(ReportingPeriods)
  .filter((value) => ![ReportingPeriods.Daily, ReportingPeriods.Weekly].includes(value))
  .map((value) => ({
    value,
    label: value,
  }))

export const ALL_REPORTING_PERIOD_OPTIONS = Object.values(ReportingPeriods).map((value) => ({
  value,
  label: value,
}))

export const ANNUALLY_REPORTING_PERIOD_OPTIONS = Object.values(AnnuallyReportingPeriods).map(
  (value) => ({
    value,
    label: value,
  }),
)

export const FILE_TYPE_TEMPLATES: Partial<
  Record<OngoingReportingType, { label: string; value: string }[]>
> = {
  [OngoingReportingType.AR]: [
    {
      label: 'AR Detail',
      value: TemplateTypes.ARDetail,
    },
    {
      label: 'AR Summary',
      value: TemplateTypes.ARSummary,
    },
  ],
  [OngoingReportingType.AP]: [
    {
      label: 'AP Detail',
      value: TemplateTypes.APDetail,
    },
    {
      label: 'AP Summary',
      value: TemplateTypes.APSummary,
    },
  ],
  [OngoingReportingType.Inventory]: [
    {
      label: 'Inventory Detail',
      value: TemplateTypes.InventoryDetail,
    },
  ],
  [OngoingReportingType.IncomeStatement]: [
    {
      label: 'Income Statement',
      value: TemplateTypes.IncomeStatement,
    },
  ],
  [OngoingReportingType.BalanceSheet]: [
    {
      label: 'Balance Sheet',
      value: TemplateTypes.BalanceSheet,
    },
  ],
  [OngoingReportingType.IncomeStatementProjections]: [
    {
      label: 'Income Statement Projections',
      value: TemplateTypes.IncomeStatement,
    },
  ],
  [OngoingReportingType.BalanceSheetProjections]: [
    {
      label: 'Balance Sheet Projections',
      value: TemplateTypes.BalanceSheet,
    },
  ],
  [OngoingReportingType.ARGeneralLedger]: [
    {
      label: 'AR General Ledger',
      value: TemplateTypes.ARGeneralLedger,
    },
  ],
  [OngoingReportingType.SalesBySKU]: [
    {
      label: 'Sales by SKU',
      value: TemplateTypes.SalesBySKU,
    },
    {
      label: 'Sales by SKU (Monthly Header)',
      value: TemplateTypes.SalesBySKUMonthlyHeader,
    },
  ],
  [OngoingReportingType.CapTable]: [
    {
      label: 'Cap Table',
      value: TemplateTypes.CapTable,
    },
  ],
}

export const ONGOING_REPORTING_STATEMENT_PROCESSING_TYPE: Partial<
  Record<OngoingReportingType, string>
> = {
  [OngoingReportingType.BalanceSheet]: 'Balance Sheet',
  [OngoingReportingType.IncomeStatement]: 'Income Statement',
  [OngoingReportingType.IncomeStatementProjections]: 'Income Statement',
  [OngoingReportingType.BalanceSheetProjections]: 'Balance Sheet',
}

export interface IOngoingReportingFinancialDates {
  [OngoingReportingType.IncomeStatement]?: {
    startDate?: string
    endDate?: string
  }
  [OngoingReportingType.BalanceSheet]?: {
    startDate?: string
    endDate?: string
  }
}

/**
 * Statement types whose file was cleared, so the next processing ignores remembered account
 * mappings: clearing deletes the rows but not the per-account memory that pre-maps a later
 * submission. Unset once the statement is processed again.
 */
export type IOngoingReportingMappingReset = Partial<Record<OngoingReportingType, boolean>>

export interface IOngoingReportingFileSheet {
  id: string
  ongoingReportingId: string
  type: string
  fileId: string
  file?: IFile
  sheetName: string
  bankAccountId?: string
  bankAccount?: IBankAccount
  data: any
}

export interface IOngoingReporting {
  id: string
  type: OngoingReportingType
  status: OngoingReportingStatus
  recordDate: string
  dueDate: string
  createdAt: string
  updatedAt: string
  completedAt: string | null
  submittedDate?: string
  clientName: string
  clientInfo?: IClientInfo
  clientIconUrl?: string
  clientId?: string
  fileSheets?: IOngoingReportingFileSheet[]
  files?: IFile[]
  boxLink?: string
  isSubmitted: boolean
  mappingRequired: boolean
  daysSinceDue?: number
  statusLabel?: string
  asOfDate?: string
  preProcessed: boolean
  /** Why pre-processing could not prepare the report. Null when it was prepared. */
  preProcessingError?: string | null
  source?: OngoingReportingSource | null
  financialDates: IOngoingReportingFinancialDates
  expirationDate?: string
  assignee?: { id: string; firstName: string; lastName: string }
}

export interface IOngoingReportingData {
  data: IOngoingReporting[]
  totals: {
    totalItems: number
    reportsSubmittedToday: number
    reportsCompletedToday: number
    reportsPending: number
  }
}

export interface IOngoingReportingRawMapping {
  id: string
  clientName: string
  clientInfo?: IClientInfo
  income: string
  field: FinancialKeys | null
  prediction: string | null
  tags: { tag: string }[]
  statementType: OngoingReportingType
  values: { period: string; amount: number }[]
  amounts?: number[]
  depth?: number
  /** The line's place on the statement, counted from 1. */
  position?: number
  fileId?: string | null
  sheetName?: string | null
  rowType?: FinancialRowTypes
  /**
   * For total/subtotal rows: which reported total this row is. Reference only —
   * the client's own figure, which the reconciliation ties Atlas's against.
   */
  referenceField?: string | null
  isNew?: boolean
  /** A detail line after the reported Net Income — the add-backs, left uncategorized. */
  isBelowBottomLine?: boolean
  /** The accounting export's "Accrual Basis … GMT" stamp, which is not a line of the statement. */
  isReportFooter?: boolean
  isConnectedToThisReport?: boolean
}

/** A line nobody has reviewed yet, in statement order; what the "new lines" arrows step through. */
export interface IOngoingReportingNewRow {
  position: number
  category: string
}

export interface IOngoingReportingRawMappingData {
  data: IOngoingReportingRawMapping[]
  dictionary: {
    headers: string[]
    headersByStatementType: {
      [OngoingReportingType.IncomeStatement]: string[]
      [OngoingReportingType.BalanceSheet]: string[]
    }
    fields: string[]
    usedFields: string[]
  }
  totals: {
    totalItems: number
    totalMapped: number
    totalFields: number
    totalUnseen?: number
    newRows?: IOngoingReportingNewRow[]
  }
  isFirstReportForClient?: boolean
  /** On the first page: the prediction run in flight for the statement, if any. */
  predictions?: { reportId: string; statementType: string; runId: string | null }
}

/** A reconciliation difference a reviewer accepted, as the mapping screens read it. */
export interface IReconciliationAcceptance<TDate = string> {
  field: string
  period: string
  difference: number
  note: string | null
  atlasExplanation: string | null
  acceptedBy: { id: string; name: string | null }
  /** A Date on the server, sent as its ISO string. */
  createdAt: TDate
}

/** A derived row of the statement a reviewer may name as a reported figure, and the one it holds. */
export interface IReportedTotalCandidate {
  id: string
  position: number
  referenceField: string | null
}

/** The same cause, accepted on an earlier report of the client. */
export interface IPreviousAcceptance<TDate = string> extends Pick<
  IReconciliationAcceptance,
  'period' | 'note' | 'atlasExplanation' | 'acceptedBy'
> {
  id: string
  periodLabel: string
  /** A Date on the server, sent as its ISO string. */
  createdAt: TDate
}

/** Another way the client's own figures give a check's figure, and the periods it lands elsewhere. */
export interface IDisagreeingRoute {
  label: string
  amounts: number[]
  disagreesIn: boolean[]
}

export interface IOngoingReportingReconciliationRow {
  field: string
  /**
   * Reported − Calculated per period. `null` where the file has no reported total for this
   * check: N/A, since treating it as zero would show a red break every month.
   */
  amounts: (number | null)[]
  instructions: string
  /** The client's own figure per period; absent exactly when `amounts` is all-null. */
  reported?: number[]
  /**
   * The route to the client's figure when not their own line ("Gross Profit + Total COGS").
   * Shown because a derived tie-out is the weaker assurance.
   */
  source?: string
  /** Other routes to this figure that disagree with the one used: most likely a mis-tagged row. */
  disagreeingRoutes?: IDisagreeingRoute[]
  /**
   * Per period, the check above whose break this one only repeats, where the
   * difference started. Null where the break is this check's own, or there is none.
   */
  carriedFrom?: (string | null)[]
}

export interface DataGrowth {
  value: number
  comparison?: {
    actual?: { value: number; label: string }
    toCompare?: { value: number; label: string }
  }
}

export interface IOngoingReportingSummaryData extends Record<FinancialKeys, number[]> {
  ongoingReportingWithDatesSummarized?: Array<{
    id: string
    date: string
    type: ReportingFlow.OngoingReporting
    label: string
  }>
  ddReportingsWithDatesSummarized?: Array<{
    id: string
    date: string
    type: ReportingFlow.DueDiligence
    label: string
  }>
  opsReportingsWithDatesSummarized?: Array<{
    id: string
    date: string
    type: ReportingFlow.OPS
    label: string
  }>
}
export interface IOngoingReportingSummaryDataGrowth extends Record<FinancialKeys, DataGrowth[]> {}

export enum FinancialsType {
  Actuals = 'Actuals',
  Projections = 'Projections',
  StressCase = 'StressCase',
}

/** Cast-free narrower for FinancialsType (see common/helpers/enumGuard). */
export const asFinancialsType = enumNarrow(FinancialsType).as

/** Store slot for a statement mounted next to another copy of the same statement type. */
export enum FinancialsSummaryScope {
  ForecastIncomeStatement = 'ForecastIncomeStatement',
  ForecastBalanceSheet = 'ForecastBalanceSheet',
  ForecastStressCase = 'ForecastStressCase',
}

export interface IOngoingReportingSummaryHeaders extends Partial<
  Record<FinancialsSummaryScope, string[]>
> {
  [OngoingReportingType.IncomeStatement]: string[]
  [OngoingReportingType.BalanceSheet]: string[]
  [OngoingReportingType.IncomeStatementProjections]: string[]
  [OngoingReportingType.BalanceSheetProjections]: string[]
  [FinancialsType.StressCase]?: string[]
}

export type FinancialsSummaryKey = keyof IOngoingReportingSummaryHeaders

export interface IOngoingReportingSummaryHeaderData {
  isProjection: boolean
  asOfDate: string
  periods: {
    [FinancialsType.Actuals]: string[]
    [FinancialsType.Projections]: string[]
    [FinancialsType.StressCase]?: string[]
  }
}

export interface IOngoingReportingSummaryHeadersData extends Partial<
  Record<FinancialsSummaryScope, IOngoingReportingSummaryHeaderData[]>
> {
  [OngoingReportingType.IncomeStatement]: IOngoingReportingSummaryHeaderData[]
  [OngoingReportingType.BalanceSheet]: IOngoingReportingSummaryHeaderData[]
  [OngoingReportingType.IncomeStatementProjections]: IOngoingReportingSummaryHeaderData[]
  [OngoingReportingType.BalanceSheetProjections]: IOngoingReportingSummaryHeaderData[]
  [FinancialsType.StressCase]?: IOngoingReportingSummaryHeaderData[]
}

export interface IOngoingReportingUpdatesItem {
  field: string
  recordDate: string
  oldValue: number
  newValue: number
  statementType: OngoingReportingType
}

export interface IncomeStatement {
  gross_revenue: number
  trade_discounts: number
  returns_refunds: number
  net_revenue: number
  dilution: number
  gross_margin: number
  product_cogs: number
  cost_of_goods_sold: number
  gross_profit: number
  total_operating_expenses: number
  payroll: number
  sales_and_marketing: number
  research_and_development: number
  other_operating_expenses: number
  operating_income: number
  interest_expense: number
  depreciation_and_amortization: number
  taxes: number
  other_expenses: number
  other_income: number
  net_income: number
  ebitda: number
  ebitda_margin: number
  collections?: number
  // Amendment-only computed metrics. Populated by the backend
  // (ongoingReporting/ListSummary) the same way `dilution`, `gross_margin`
  // and `ebitda_margin` are.
  net_revenue_growth: number
  return_on_ad_spend: number
  opex_pct_net_revenue: number
  // Marketing / Payroll as a percent of Net Revenue, and COGS as a percent of
  // Gross Revenue. Computed per reporting period by the backend
  // (clients/ListFinancialsSummary) the same way as the metrics above, but rendered
  // at the bottom of the Income Statement summary on the standard Financials tab
  // (not amendment-only).
  marketing_pct_net_revenue: number
  payroll_pct_net_revenue: number
  cogs_pct_gross_revenue: number
}

export interface BalanceSheet {
  cash_and_cash_equivalents: number
  accounts_receivable_net: number
  inventory: number
  prepaid_expenses: number
  other_current_assets: number
  total_current_assets: number
  net_ppe: number
  intangible_goodwill: number
  other_long_term_assets: number
  total_long_term_assets: number
  total_assets: number
  accounts_payable: number
  accrued_expenses: number
  taxes_payable: number
  deferred_revenue_and_deposits: number
  dwight_funding_loan: number
  credit_card_debt: number
  gift_cards: number
  sba_loans: number
  other_short_term_debt: number
  other_current_liabilities: number
  total_current_liabilities: number
  convertible_notes_debt: number
  ppp: number
  eidl: number
  other_long_term_debt: number
  other_long_term_liabilities: number
  total_long_term_liabilities: number
  total_liabilities: number
  common_and_treasury_stock: number
  additional_paid_in_capital: number
  convertible_notes_equity: number
  retained_earnings: number
  other_shareholders_equity: number
  total_shareholders_equity: number
  quick_ratio: number
  current_ratio: number
  // Amendment-only computed metrics (see comment on IncomeStatement).
  liabilities_over_equity: number
  weeks_of_supply: number
}

interface FinancialReconcileSummary {
  assets: string
  cashflow: string
  // Not a statement line: the sentinel for a memo row kept out of every category.
  excluded: string
}

interface CashFlowSummary {
  change_in_cash: number
  change_in_cash_on_bs: number
}

/**
 * Totals a client states that Atlas never derives, giving a reconciliation a second route
 * (Gross Profit + Total COGS without a Net Revenue line). Deliberately not in
 * `FinancialKeys`: nothing maps to them and no column persists them.
 */
interface FinancialReportedTotal {
  total_gross_revenue: number
  total_trade_discounts: number
  total_returns_refunds: number
  total_cost_of_goods_sold: number
  income_before_taxes: number
  total_interest_expense: number
  total_taxes: number
  total_other_income: number
  total_other_expenses: number
  net_other_income: number
  total_liabilities_and_equity: number
}

export type BalanceSheetKeys = keyof BalanceSheet
export type IncomeStatementKeys = keyof IncomeStatement
type CashFlowSummaryKeys = keyof CashFlowSummary
type FinancialReconcileSummaryKeys = keyof FinancialReconcileSummary
export type FinancialReportedTotalKeys = keyof FinancialReportedTotal
export type FinancialKeys = BalanceSheetKeys | IncomeStatementKeys | CashFlowSummaryKeys
/** A key a figure on the client's statement can carry: one of Atlas's own figures, or a total only the client states. */
export type ReportedFigureKey = FinancialKeys | FinancialReportedTotalKeys
type FinancialMappingKeys =
  FinancialKeys | FinancialReconcileSummaryKeys | FinancialReportedTotalKeys

export const CLIENT_FINANCIALS_FIELDS_MAPPING: Partial<Record<FinancialMappingKeys, string>> = {
  gross_revenue: 'Gross Revenue',
  trade_discounts: 'Trade Discounts',
  returns_refunds: 'Returns Refunds',
  net_revenue: 'Net Revenue',
  dilution: 'Dilution',
  product_cogs: 'Product COGS',
  cost_of_goods_sold: 'Cost of Goods Sold',
  gross_profit: 'Gross Profit',
  total_operating_expenses: 'Total Operating Expenses',
  payroll: 'Payroll',
  sales_and_marketing: 'Sales & Marketing',
  research_and_development: 'Research & Development',
  other_operating_expenses: 'Other OpEx',
  operating_income: 'Operating Income',
  interest_expense: 'Interest Expense',
  depreciation_and_amortization: 'Depreciation & Amortization',
  taxes: 'Taxes',
  other_expenses: 'Other Expenses',
  other_income: 'Other Income',
  net_income: 'Net Income',
  cash_and_cash_equivalents: 'Cash & Cash Equivalents',
  accounts_receivable_net: 'Accounts Receivable Net',
  inventory: 'Inventory',
  total_current_assets: 'Total Current Assets',
  other_current_assets: 'Other Current Assets',
  net_ppe: 'Net Ppe',
  other_long_term_assets: 'Other Long Term Assets',
  total_long_term_assets: 'Total Long Term Assets',
  total_assets: 'Total Assets',
  assets: 'A=L+SE',
  cashflow: 'Cashflow',
  excluded: 'Excluded (memo)',
  // Reference figures, not categories: second routes for a check whose figure has no line.
  total_gross_revenue: 'Total Gross Revenue',
  total_trade_discounts: 'Total Discounts',
  total_returns_refunds: 'Total Returns',
  total_cost_of_goods_sold: 'Total COGS',
  income_before_taxes: 'Income Before Taxes',
  total_interest_expense: 'Total Interest Expense',
  total_taxes: 'Total Taxes',
  total_other_income: 'Total Other Income',
  total_other_expenses: 'Total Other Expenses',
  net_other_income: 'Net Other Income',
  total_liabilities_and_equity: 'Total Liabilities and Equity',
  accounts_payable: 'Accounts Payable',
  other_current_liabilities: 'Other Current Liabilities',
  total_current_liabilities: 'Total Current Liabilities',
  other_long_term_liabilities: 'Other Long Term Liabilities',
  total_long_term_liabilities: 'Total Long Term Liabilities',
  total_liabilities: 'Total Liabilities',
  accrued_expenses: 'Accrued Expenses',
  deferred_revenue_and_deposits: 'Deferred Revenue & Deposits',
  dwight_funding_loan: 'Dwight Funding Loan',
  credit_card_debt: 'Credit Card Debt',
  other_short_term_debt: 'Other Short Term Debt',
  other_long_term_debt: 'Other Long Term Debt',
  total_shareholders_equity: 'Total Shareholders Equity',
  other_shareholders_equity: 'Other Shareholders Equity',
  convertible_notes_debt: 'Convertible Notes Debt',
  common_and_treasury_stock: 'Common & Treasury Stock',
  additional_paid_in_capital: 'Additional Paid in Capital',
  convertible_notes_equity: 'Convertible Notes Equity',
  retained_earnings: 'Retained Earnings',
  taxes_payable: 'Taxes Payable',
  gift_cards: 'Gift Cards',
  sba_loans: 'SBA Loans',
  intangible_goodwill: 'Intangible Goodwill',
  prepaid_expenses: 'Prepaid Expenses',
  ppp: 'PPP',
  eidl: 'EIDL',
  ebitda: 'EBITDA',
  gross_margin: 'Gross Margin',
  ebitda_margin: 'EBITDA Margin',
  quick_ratio: 'Quick Ratio',
  current_ratio: 'Current Ratio',
  collections: 'Collections',
  net_revenue_growth: 'Net Revenue Growth',
  return_on_ad_spend: 'Return on Ad Spend',
  opex_pct_net_revenue: 'OpEx % of Net Revenue',
  marketing_pct_net_revenue: 'Marketing % of Net Revenue',
  payroll_pct_net_revenue: 'Payroll % of Net Revenue',
  cogs_pct_gross_revenue: 'COGS % of Gross Revenue',
  liabilities_over_equity: 'Liabilities / Equity',
  weeks_of_supply: 'Est. Weeks of Supply',
}

/** A balance sheet or its projection: the two read the same way everywhere. */
export const isBalanceSheetType = (statementType?: string | null) =>
  statementType === OngoingReportingType.BalanceSheet ||
  statementType === OngoingReportingType.BalanceSheetProjections

/** An income statement or its projection. */
export const isIncomeStatementType = (statementType?: string | null) =>
  statementType === OngoingReportingType.IncomeStatement ||
  statementType === OngoingReportingType.IncomeStatementProjections

/** A projection, of either statement. */
export const isProjectionsType = (statementType?: string | null) =>
  statementType === OngoingReportingType.BalanceSheetProjections ||
  statementType === OngoingReportingType.IncomeStatementProjections

/** The display label for a mapped category, or the raw key when there is none. */
export const getFinancialFieldLabel = (field: string | null | undefined): string =>
  (field && CLIENT_FINANCIALS_FIELDS_MAPPING[field as FinancialMappingKeys]) || field || ''

export const CLIENT_INCOME_STATEMENT_TAGS_MAPPING = {
  gross_revenue_dtc: 'Gross Rev - DTC',
  gross_revenue_wholesale: 'Gross Rev - Wholesale',
  gross_revenue_general: 'Gross Rev - General',
  gross_revenue_shipping_income: 'Gross Rev - Shipping Income',

  tradespend_dtc_discounts: 'TS - DTC Discounts',
  tradespend_dtc_returns_refunds: 'TS - DTC Returns & Refunds',
  tradespend_wholesale_discounts: 'TS - Wholesale Discounts',
  tradespend_wholesale_returns_refunds: 'TS - Wholesale Returns & Refunds',
  tradespend_omni_discount: 'TS - Omni Discount',
  tradespend_omni_returns_refunds: 'TS - Omni Returns & Refunds',

  net_revenue_dtc: 'Net Rev - DTC',
  net_revenue_wholesale: 'Net Rev - Wholesale',
  net_revenue_general: 'Net Rev - General',

  cogs_dtc: 'Channel COGS - DTC',
  cogs_wholesale: 'Channel COGS - Wholesale',
  cogs_general: 'Channel COGS - General',

  cogs_product: 'Comp. COGS - Product COGS',
  cogs_packaging: 'Comp. COGS - Packaging/Raw Materials',
  cogs_freight: 'Comp. COGS - Freight-In',
  cogs_inventory: 'Comp. COGS - Inventory Adjustment',
  cogs_duty: 'Comp. COGS - Duty',
  cogs_direct_labor: 'Comp. COGS - Direct Labor',

  selling_expense_warehouse_and_fulfillment: 'SE - Warehouse/Fulfillment/3PL',
  selling_expense_merchant_fees: 'SE - Merchant Fees',
  selling_expense_freight_out: 'SE - Freight-Out',
  selling_expense_general: 'SE - General',

  employee_costs_payroll: 'Employee Costs - Payroll',
  employee_costs_commision_payments: 'Employee Costs - Commission Payments',
  employee_costs_bonus: 'Employee Costs - Bonus',
  employee_costs_te: 'Employee Costs - T&E',
  employee_costs_general: 'Employee Costs - General',

  professional_fees_legal: 'Prof. Fees - Legal',
  professional_fees_accounting_finance: 'Prof. Fees - Accounting & Finance',
  professional_fees_recruiting: 'Prof. Fees - Recruiting',
  professional_fees_general: 'Prof. Fees - General',

  marketing_pr: 'Marketing - Public Relations',
  marketing_online_social: 'Marketing - Online/Social',
  marketing_print: 'Marketing - Print',
  marketing_influencer: 'Marketing - Influencer',
  marketing_photography_and_models: 'Marketing - Photography & Models',
  marketing_agencies: 'Marketing - Agencies',
  marketing_events: 'Marketing - Events',
  marketing_samples: 'Marketing - Samples',
  marketing_general: 'Marketing - General',

  platform_facebook: 'Platform - Facebook',
  platform_instagram: 'Platform - Instagram',
  platform_google: 'Platform - Google',
  platform_tiktok: 'Platform - TikTok',
  platform_bing: 'Platform - Bing',
  platform_amazon: 'Platform - Amazon',
  platform_youtube: 'Platform - YouTube',
  platform_twitter: 'Platform - Twitter',

  occupancy_rent: 'Occupancy - Rent',
  occupancy_repairs_and_maintenance: 'Occupancy - Repairs & Maintenance',
  occupancy_office_supplies: 'Occupancy - Office Supplies',
  occupancy_general: 'Occupancy - General',

  technology_hardware: 'Technology - Hardware',
  technology_software: 'Technology - Software',
  technology_website: 'Technology - Website',
  technology_general: 'Technology - General',

  misc_expense_insurance: 'Misc. Expense - Insurance',
  misc_expense_charitable_contributions: 'Misc. Expense - Charitable Contributions',
  misc_expense_dues_and_subscriptions: 'Misc. Expense - Dues & Subscriptions',
  misc_expense_general: 'Misc. Expense - General',
}

export const QUARTERS_MAPPING = {
  Q1: [0, 1, 2],
  Q2: [3, 4, 5],
  Q3: [6, 7, 8],
  Q4: [9, 10, 11],
}

export const CLIENT_BALANCE_SHEET_TAGS_MAPPING = {
  inventory_finished_goods: 'Inventory - Finished Goods',
  inventory_wip: 'Inventory - WIP',
  inventory_packaging: 'Inventory - Packaging',
  inventory_raw_materials: 'Inventory - Raw Materials',
  inventory_freight: 'Inventory - Freight',
  inventory_direct_labor: 'Inventory - Direct Labor',
  inventory_in_transit: 'Inventory - In Transit',
  inventory_other: 'Inventory - Other',
  inventory_prepaid_inventory: 'Inventory - Prepaid Inventory',
  inventory_general: 'Inventory - General',
  inventory_reserves_obsolescence: 'Inventory - Reserves/Obsolescence',

  apic_seed: 'APIC - Seed',
  apic_series_a: 'APIC - Series A',
  apic_series_b: 'APIC - Series B',
  apic_series_c: 'APIC - Series C',
  apic_series_d: 'APIC - Series D',
  apic_series_e: 'APIC - Series E',
  apic_series_f: 'APIC - Series F',
  apic_general: 'APIC - General',
}

export const CLIENT_FINANCIALS_HEADER_FIELDS: FinancialKeys[] = [
  'gross_revenue',
  'net_revenue',
  'gross_profit',
  'total_operating_expenses',
  'operating_income',
  'net_income',
  'total_current_assets',
  'total_long_term_assets',
  'total_assets',
  'total_current_liabilities',
  'total_long_term_liabilities',
  'total_liabilities',
  'total_shareholders_equity',
  'gross_margin',
  'dilution',
  // Amendment-only metrics. Listed here so the row component's visibility
  // gate treats them as top-level rows when the amendment view explicitly
  // iterates them; they are not iterated in any other context.
  'net_revenue_growth',
  'return_on_ad_spend',
  'opex_pct_net_revenue',
  'marketing_pct_net_revenue',
  'payroll_pct_net_revenue',
  'cogs_pct_gross_revenue',
  'liabilities_over_equity',
  'weeks_of_supply',
]
export const CLIENT_FINANCIALS_HEADER_FIELDS_WITH_EXPAND: FinancialKeys[] = [
  'gross_revenue',
  'dilution',
  'gross_margin',
  'operating_income',
  'total_current_assets',
  'total_assets',
  'total_current_liabilities',
  'total_liabilities',
]
export const CLIENT_FINANCIALS_DETAILS_FIELDS_EXPANDERS: Partial<Record<FinancialKeys, string>> = {
  trade_discounts: 'gross_revenue',
  returns_refunds: 'gross_revenue',
  product_cogs: 'dilution',
  cost_of_goods_sold: 'dilution',
  payroll: 'gross_margin',
  sales_and_marketing: 'gross_margin',
  research_and_development: 'gross_margin',
  other_operating_expenses: 'gross_margin',
  interest_expense: 'operating_income',
  depreciation_and_amortization: 'operating_income',
  taxes: 'operating_income',
  other_expenses: 'operating_income',
  other_income: 'operating_income',
  ebitda_margin: 'operating_income',
  cash_and_cash_equivalents: 'total_current_assets_above',
  accounts_receivable_net: 'total_current_assets_above',
  inventory: 'total_current_assets_above',
  prepaid_expenses: 'total_current_assets_above',
  other_current_assets: 'total_current_assets_above',
  net_ppe: 'total_current_assets',
  intangible_goodwill: 'total_current_assets',
  other_long_term_assets: 'total_current_assets',
  accounts_payable: 'total_assets',
  accrued_expenses: 'total_assets',
  taxes_payable: 'total_assets',
  deferred_revenue_and_deposits: 'total_assets',
  dwight_funding_loan: 'total_assets',
  credit_card_debt: 'total_assets',
  gift_cards: 'total_assets',
  sba_loans: 'total_assets',
  other_short_term_debt: 'total_assets',
  other_current_liabilities: 'total_assets',
  convertible_notes_debt: 'total_current_liabilities',
  ppp: 'total_current_liabilities',
  eidl: 'total_current_liabilities',
  other_long_term_debt: 'total_current_liabilities',
  other_long_term_liabilities: 'total_current_liabilities',
  total_long_term_liabilities: 'total_current_liabilities',
  common_and_treasury_stock: 'total_liabilities',
  additional_paid_in_capital: 'total_liabilities',
  convertible_notes_equity: 'total_liabilities',
  retained_earnings: 'total_liabilities',
  other_shareholders_equity: 'total_liabilities',
  quick_ratio: 'total_liabilities',
  current_ratio: 'total_liabilities',
}

export const CLIENT_FINANCIALS_HEADER_FIELDS_WITH_EXPAND_BY_TYPE: Partial<
  Record<OngoingReportingType, (FinancialKeys | 'total_current_assets_above')[]>
> = {
  [OngoingReportingType.IncomeStatement]: [
    'gross_revenue',
    'dilution',
    'gross_margin',
    'operating_income',
  ],
  [OngoingReportingType.BalanceSheet]: [
    'total_current_assets_above',
    'total_current_assets',
    'total_assets',
    'total_current_liabilities',
    'total_liabilities',
  ],
}

export const CLIENT_FINANCIALS_BOTTOM_LINE_FIELDS = ['quick_ratio', 'current_ratio']

export const CLIENT_FINANCIALS_PERCENT_FIELDS: FinancialKeys[] = [
  'dilution',
  'gross_margin',
  'ebitda_margin',
  'net_revenue_growth',
  'opex_pct_net_revenue',
  'marketing_pct_net_revenue',
  'payroll_pct_net_revenue',
  'cogs_pct_gross_revenue',
]

export const CLIENT_FINANCIALS_RATIO_FIELDS: FinancialKeys[] = [
  'quick_ratio',
  'current_ratio',
  'return_on_ad_spend',
  'liabilities_over_equity',
]

// Ratio metrics computed from an underlying numerator/denominator (not stored columns). Their
// portfolio totals must be weighted averages — SUM(numerator) / SUM(denominator) — not an average
// of per-client ratios. Single source of truth shared by the financials-comparison service
// (totals SQL) and its frontend (weighted summary rows), so the two cannot drift.
export const CALCULATED_FINANCIAL_METRICS: FinancialKeys[] = [
  'gross_margin',
  'dilution',
  'ebitda_margin',
  'quick_ratio',
  'current_ratio',
]

// Rendered with a "wks" suffix instead of "x" / "%" / "$".
export const CLIENT_FINANCIALS_WEEKS_FIELDS: FinancialKeys[] = ['weeks_of_supply']

// Computed metrics appended after the main statement rows when the
// component runs in amendment mode. Backend populates the same dataSummary
// fields unconditionally; the frontend only iterates these in that mode.
export const AMENDMENT_EXTRA_FIELDS_BY_TYPE: Partial<
  Record<OngoingReportingType, FinancialKeys[]>
> = {
  [OngoingReportingType.IncomeStatement]: [
    'net_revenue_growth',
    'return_on_ad_spend',
    'opex_pct_net_revenue',
  ],
  [OngoingReportingType.BalanceSheet]: ['liabilities_over_equity', 'weeks_of_supply'],
}

// Derived percent metrics appended after the main statement rows on the
// standard Financials tab (unlike AMENDMENT_EXTRA_FIELDS_BY_TYPE, which the
// frontend renders only in amendment mode). Backend populates these
// dataSummary fields unconditionally, the same way as the amendment extras.
export const CLIENT_FINANCIALS_SUMMARY_EXTRA_FIELDS_BY_TYPE: Partial<
  Record<OngoingReportingType, FinancialKeys[]>
> = {
  [OngoingReportingType.IncomeStatement]: [
    'marketing_pct_net_revenue',
    'payroll_pct_net_revenue',
    'cogs_pct_gross_revenue',
  ],
}

export const CLIENT_FINANCIALS_FIELDS: Partial<
  Record<OngoingReportingType, (IncomeStatementKeys | BalanceSheetKeys)[]>
> = {
  [OngoingReportingType.IncomeStatement]: [
    'gross_revenue',
    'trade_discounts',
    'returns_refunds',
    'net_revenue',
    'dilution',
    'product_cogs',
    'cost_of_goods_sold',
    'gross_profit',
    'gross_margin',
    'payroll',
    'sales_and_marketing',
    'research_and_development',
    'other_operating_expenses',
    'total_operating_expenses',
    'operating_income',
    'interest_expense',
    'depreciation_and_amortization',
    'taxes',
    'other_expenses',
    'other_income',
    'net_income',
    'ebitda_margin',
    'collections',
  ],
  [OngoingReportingType.BalanceSheet]: [
    'cash_and_cash_equivalents',
    'accounts_receivable_net',
    'inventory',
    'prepaid_expenses',
    'other_current_assets',
    'total_current_assets',
    'net_ppe',
    'intangible_goodwill',
    'other_long_term_assets',
    'total_long_term_assets',
    'total_assets',
    'accounts_payable',
    'accrued_expenses',
    'taxes_payable',
    'deferred_revenue_and_deposits',
    'dwight_funding_loan',
    'credit_card_debt',
    'gift_cards',
    'other_short_term_debt',
    'other_current_liabilities',
    'total_current_liabilities',
    'convertible_notes_debt',
    'ppp',
    'eidl',
    'other_long_term_debt',
    'other_long_term_liabilities',
    'total_long_term_liabilities',
    'total_liabilities',
    'common_and_treasury_stock',
    'additional_paid_in_capital',
    'convertible_notes_equity',
    'retained_earnings',
    'other_shareholders_equity',
    'total_shareholders_equity',
    'quick_ratio',
    'current_ratio',
  ],
}

export interface IFundingRequest {
  id: string
  status: FundingRequestStatus
  amount: number
  amountType: FundingRequestAmountType
  files: { [key: string]: IFile | IFile[] }
  dueReports: IRequiredReport[]
  bbcSignatureFile?: IFile
}

export interface IARAPReconcileSummary {
  totalFromReport?: number
  totalCalculated?: number
  prebilling?: number
}

export interface IBorrowingBaseFileSheet {
  id: string
  borrowingBaseId: string
  type: string
  fileId: string
  file?: IFile
  sheetName: string
  data: any
  createdAt: string
  updatedAt: string
}

export interface IBorrowingBase {
  id: string
  requestedAmount: number
  requestedAmountType: FundingRequestAmountType
  status: FundingRequestStatus
  recordDate: string
  clientName: string
  clientInfo?: IClientInfo
  files?: IFile[]
  fileSheets?: IBorrowingBaseFileSheet[]
  clientCollateral?: IClientCollateral
  isCollateralEligibilityReviewRequired?: boolean
  isCollateralEligibilityHasCriticalDebtor?: boolean
  isEntityMappingRequired?: boolean
  isDebtorMappingRequired?: boolean
  isCreditorMappingRequired?: boolean
  inventoryConverterTemplateId?: string | null
  arReconcileSummary: IARAPReconcileSummary
  apReconcileSummary: IARAPReconcileSummary
  boxLink?: string | null
  previousBBC: IBorrowingBase
  clientBoxLink?: string
  preProcessed: boolean
  /** Set while a pre-processing run is in flight, null otherwise. */
  preProcessingStartedAt?: string | null
  /** When the queue will terminate that run — the hard ceiling, not an estimate. */
  preProcessingDeadlineAt?: string | null
  /**
   * When the run is expected to finish, from this client's recent successful
   * runs. Null when there is not enough history. Advisory: passing it means the
   * run is slow, not that it has failed.
   */
  preProcessingEtaAt?: string | null
  /** Why the last run did not finish. Null when it completed. */
  preProcessingError?: string | null
  /**
   * Which collateral sections pre-processing has finished with. See
   * {@link filterPreProcessingSections} for the null-vs-empty distinction.
   */
  preProcessingReadySections?: OngoingReportingType[] | null
  postProcessing: boolean
  postProcessingFailed?: boolean
  fundings?: IFunding[]
  wireCount: number
  isBlocked: boolean
  isTest: boolean
  projectedNewLoanAmount: number
  isReminderEnabled?: boolean
  createdAt: string
  activity?: IActivity
}

export interface IBorrowingBaseIndex {
  id: string
  clientName: string
  clientId: string
  clientIconUrl: string
  status: FundingRequestStatus
  isTest: boolean
  amount: number
  recordDate: string
  boxLink: string | null
  arAvailability?: number
  inventoryAvailabilityPreSubLimit?: number
  inventoryAvailabilityPostSubLimit?: number
  keywords: string
}

export interface IFunding {
  id: string
  borrowingBaseId: string | null
  borrowingBase: IBorrowingBase | null
  amount: number
  sentDate?: string
  feeId: string | null
  fee: IFee | null
  description: string
  createdAt: string
}

export interface IBorrowingBaseStats {
  contras: string[]
  ineligibleReceivables: {
    debtor: string
    category: string
    eligible: boolean
    notesReason: string
  }[]
  ineligibleInventory: {
    type: string
    description: string
  }[]
  priorityPayables: string[]
  ineligibleLocations: string[]
}

export interface IClientCollateral {
  id: string
  clientName: string
  borrowingBaseId?: string
  borrowingBase?: IBorrowingBase
  recordDate: string
  arOtherReservesFixed: number
  arOtherReservesPercent: number
  arOtherReservesField: string
  invOtherReservesFixed: number
  invOtherReservesPercent: number
  invOtherReservesField: string
  totalReceivables: number
  lessIneligibleReceivables: number
  eligibleReceivables: number
  arAdvanceRate: number
  arAvailability: number
  ar_1To_30Days: number
  ar_31To_60Days: number
  ar_61To_90Days: number
  ar_91PlusDays: number
  totalGrossAr: number
  ap_0To_60Days: number
  ap_1To_30Days: number
  ap_31To_60Days: number
  ap_61To_90Days: number
  ap_91PlusDays: number
  inv_0To_90DaysSinceLanded: number
  inv_91To_180DaysSinceLanded: number
  inv_181To_270DaysSinceLanded: number
  inv_271To_360DaysSinceLanded: number
  inv_361PlusDaysSinceLanded: number
  inv_361PlusDaysUntilExpiration: number
  inv_271To_360DaysUntilExpiration: number
  inv_181To_270DaysUntilExpiration: number
  inv_91To_180DaysUntilExpiration: number
  inv_0To_90DaysUntilExpiration: number
  totalAp: number
  finishedGoods: number
  rawMaterials: number
  other: number
  totalGrossInventory: number
  totalInventory: number
  ineligibleSkus: number
  ineligibleLocationsAndInTransit: number
  slowAgedOrExpired: number
  over_90Payables: number
  invOtherMiscReserves: number
  totalIneligibleInventory: number
  over_90DaysAndCreditsPastDue: number
  crossAge: number
  ineligibleDebtors: number
  contras: number
  concentration: number
  creditLimits: number
  arOtherMiscReserves: number
  totalIneligibleAr: number
  eligibleInventoryCost: number
  costValueOfFinishedGoodsInventory: number
  costValueOfRawMaterialsInventory: number
  costValueOfInventory: number
  inventoryAdvanceRate: number
  rawMaterialsAdvanceRate: number
  nolvAdvanceFinishedGoods: number
  nolvAdvanceRawMaterials: number
  nolvAdvance: number
  nolvAdvanceRate: number
  nolvPercentOfCost: number
  rmNolv: number
  rmInventoryAdvanceRateNolv: number
  nolvCalculationFinishedGoods: number
  nolvCalculationRawMaterials: number
  nolvCalculation: number
  inventoryAvailabilityPreSubLimit: number
  inventoryAvailabilityPostSubLimit: number
  /** The client's sublimit when this BBC was processed, as a multiple of AR availability. */
  inventorySublimit: number | null
  netAvailability: number
  netAvailabilityWithOveradvances: number
  maximumLine: number
  finalNetAvailability: number
  excessLoanAvailability: number
  currentLoanAmount: number
  newLoanAmount: number
  wireRequest: number | null
  wireCosts: number
  priorLoanAmount: number
  realizedInterest: number
  miscFees: number
  collections: number
  adjustments: number
  returnedCollections: number
  termLoanPrincipal: number
  termLoanBalance: number
  totalCurrentLoanBalance: number
  overadvances?: number[]
}

export interface IPreviousCollateralDateOptions {
  recordDate: string
}

export enum Category {
  Foreign = 'Foreign',
  Government = 'Government',
  Intercompany = 'Intercompany',
  Other = 'Other',
  Distressed = 'Distressed',
  Affiliate = 'Affiliate',
}

export enum PrecedentVerdict {
  Likely = 'likely',
  Unlikely = 'unlikely',
  Split = 'split',
  Unknown = 'unknown',
}

export interface IPrecedentValue {
  value: number
  clients: number
  /** Capped — compare against `clients` to know whether more chose this value. */
  clientNames: string[]
}

export interface IPrecedentLimit {
  suggested: number | null
  avg: number
  min: number
  max: number
  values: IPrecedentValue[]
}

/**
 * What the rest of the portfolio already decided about this debtor, for a row that
 * still needs mapping. Advisory: nothing here is written to the row without the
 * analyst clicking to accept it. Excludes the client being mapped.
 */
export interface IDebtorPrecedent {
  clientCount: number
  eligibleClients: number
  ineligibleClients: number
  /** Capped — compare against the counts above to know whether more exist. */
  eligibleClientNames: string[]
  ineligibleClientNames: string[]
  verdict: PrecedentVerdict
  categories: { value: string; clients: number; clientNames: string[] }[]
  suggestedCategory: string | null
  creditLimits: IPrecedentLimit | null
  concentrationLimits: IPrecedentLimit | null
}

/**
 * Where a debtor looks to be headquartered, for a new customer no other client has
 * mapped yet. Advisory: the analyst opens `sourceUrl` to check the call themselves.
 */
export interface IHqSuggestion {
  city: string | null
  state: string | null
  country: string | null
  sourceUrl: string | null
  confidence: number | null
  reasoning: string | null
  verdict: PrecedentVerdict
  label: string
}

export interface IArIneligibility {
  id: string
  category: string
  eligible: boolean
  creditLimits: number
  concentrationLimit: number
  totalAR: number
  totalIneligible: number
  notesReason: string
  isCritical: boolean
  isContra: boolean
  entityInfo?: IEntityInfo
  lastActive?: string
  lastAmount?: number
  mappingRequired?: boolean
  precedent?: IDebtorPrecedent
  hqSuggestion?: IHqSuggestion
}

export interface IArIneligibilityData {
  data: IArIneligibility[]
  dictionary: {
    debtors: string[]
  }
  totals: {
    totalItems: number
  }
}

export interface IExtraReserve {
  id?: string
  entityId?: string
  creditor: string
  priorityPayable?: string
  over0Payables?: number
  over30Payables?: number
  over60Payables?: number
  over90Payables?: number
  payablesReserve?: number
  calculatedPayablesReserve?: number
  isManualOverride?: boolean
  isCustom?: boolean
  customReserveId?: string
  overrideReserveId?: string
  notes?: string
  lastAmount?: number
  lastActive?: string
  type?: string
  entityInfo?: IEntityInfo
}

export interface IExtraReserveData {
  data: IExtraReserve[]
  dictionary: {
    creditors: string[]
  }
  totals: { totalItems: number }
}

export enum SkuTypes {
  Finished = 'Finished Goods',
  InProcess = 'Work in Process',
  Raw = 'Raw Materials',
  Packaging = 'Packaging',
  Other = 'Other/Unspecified',
}

export const SKU_TYPES_LIST = [
  {
    value: SkuTypes.Finished,
    label: SkuTypes.Finished,
  },
  {
    value: SkuTypes.InProcess,
    label: SkuTypes.InProcess,
  },
  {
    value: SkuTypes.Raw,
    label: SkuTypes.Raw,
  },
  {
    value: SkuTypes.Packaging,
    label: SkuTypes.Packaging,
  },
  {
    value: SkuTypes.Other,
    label: SkuTypes.Other,
  },
]

export interface IInventoryIneligibility {
  id: string
  brandCategory: string
  recordDate: string
  sku: string
  value: string
  type?: string
  notes?: string
  description?: string
  location?: string
  totalValue?: number
  eligible: boolean
  clientName: string
  clientInfo?: IClientInfo
  isNew?: boolean
  foreignCountry?: string
  isBankEligible?: boolean
  lastActive?: string
  lastAmount?: number
  inventoryLocation?: IInventoryLocation
  /** Suggested type awaiting ops review. Never the value the borrowing base reads — that is `type`. */
  predictedType?: string | null
  predictionConfidence?: 'high' | 'medium' | 'low' | null
  predictionMetadata?: IInventoryTypePredictionMetadata | null
}

export interface IInventoryIneligibilityData {
  data: IInventoryIneligibility[]
  totals: {
    totalItems: number
    /**
     * Outstanding type suggestions for the whole client, not the loaded page — accepting is
     * client-scoped, so the count that labels the button has to be too.
     */
    pendingSuggestions?: number
  }
  invalid?: boolean
  categories?: string[]
  vendors?: string[]
  skusChanged?: string
}

export const NOT_ALLOWED_INVENTORY_INELIGIBILITY_FIELDS = [
  'sku',
  'lotNumber',
  'onHand',
  'avgUnitCost',
  'totalValue',
  'daysSinceLanded',
  'daysUntilExpiration',
  'weeksOfSupply',
  'sales',
  'salesUnits',
  'expirationDate',
  'landedDate',
  'recordDate',
]

export const INVENTORY_INELIGIBILITY_FIELDS: Record<
  string,
  {
    category: string
    dbField: string
    reason: string
  }
> = {
  sku: {
    category: 'Unique SKUs',
    dbField: 'sku',
    reason: 'SKU',
  },
  description: {
    category: 'Unique Descriptions',
    dbField: 'description',
    reason: 'Description',
  },
  location: {
    category: 'Unique Locations',
    dbField: 'location',
    reason: 'Location',
  },
  type: {
    category: 'Unique Types',
    dbField: 'type',
    reason: 'Type',
  },
  season: {
    category: 'Unique Seasons',
    dbField: 'season',
    reason: 'Season',
  },
  brandCategory: {
    category: 'Unique Category',
    dbField: 'brand_category',
    reason: 'Category',
  },
  industrySubcategory: {
    category: 'Unique Sub Category',
    dbField: 'industry_subcategory',
    reason: 'Sub Category',
  },
  style: {
    category: 'Unique Style',
    dbField: 'style',
    reason: 'Style',
  },
  size: {
    category: 'Unique Sizes',
    dbField: 'size',
    reason: 'Size',
  },
  flavor: {
    category: 'Unique Flavor',
    dbField: 'flavor',
    reason: 'Flavor',
  },
  color: {
    category: 'Unique Color',
    dbField: 'color',
    reason: 'Color',
  },
  lotNumber: {
    category: 'Unique Lot Numbers',
    dbField: 'lot_number',
    reason: 'Lot Number',
  },
  custom_1: {
    category: 'Custom 1',
    dbField: 'custom_1',
    reason: 'Custom 1',
  },
  custom_2: {
    category: 'Custom 2',
    dbField: 'custom_2',
    reason: 'Custom 2',
  },
  custom_3: {
    category: 'Custom 3',
    dbField: 'custom_3',
    reason: 'Custom 3',
  },
  custom_4: {
    category: 'Custom 4',
    dbField: 'custom_4',
    reason: 'Custom 4',
  },
  custom_5: {
    category: 'Custom 5',
    dbField: 'custom_5',
    reason: 'Custom 5',
  },
  onHand: {
    category: 'Unique On Hands',
    dbField: 'on_hand',
    reason: 'On Hand',
  },
  avgUnitCost: {
    category: 'Unique Average Unit Costs',
    dbField: 'avg_unit_cost',
    reason: 'Average Unit Cost',
  },
  totalValue: {
    category: 'Unique Total Values',
    dbField: 'total_value',
    reason: 'Total Value',
  },
  landedDate: {
    category: 'Unique Landed Dates',
    dbField: 'landed_date',
    reason: 'Landed Date',
  },
  expirationDate: {
    category: 'Unique Expiration Dates',
    dbField: 'expiration_date',
    reason: 'Expiration Date',
  },
  daysSinceLanded: {
    category: 'Unique Days Since Landed',
    dbField: 'days_since_landed',
    reason: 'Days Since Landed',
  },
  daysUntilExpiration: {
    category: 'Unique Days Until Expiration',
    dbField: 'days_until_expiration',
    reason: 'Days Until Expiration',
  },
  sales: {
    category: 'Unique Sales',
    dbField: 'sales_amount',
    reason: 'Sales',
  },
  salesUnits: {
    category: 'Unique Sales Units',
    dbField: 'sales_units',
    reason: 'Sales Units',
  },
  weeksOfSupply: {
    category: 'Unique Weeks Of Supply',
    dbField: 'weeks_of_supply',
    reason: 'Weeks Of Supply',
  },
  department: {
    category: 'Unique Department',
    dbField: 'department',
    reason: 'Department',
  },
  dwightCategory: {
    category: 'Unique Standard Category',
    dbField: 'dwight_category',
    reason: 'Standard Category',
  },
  dwightSubcategory: {
    category: 'Unique Standard Sub Category',
    dbField: 'dwight_subcategory',
    reason: 'Standard Sub Category',
  },
  dwightSize: {
    category: 'Unique Standard Size',
    dbField: 'dwight_size',
    reason: 'Standard Size',
  },
  dwightDepartment: {
    category: 'Unique Standard Department',
    dbField: 'dwight_department',
    reason: 'Standard Department',
  },
}

export type InventoryFieldKeys = keyof typeof INVENTORY_INELIGIBILITY_FIELDS

export const INVENTORY_DISTRIBUTION_LABELS: Record<InventoryFieldKeys, string> = Object.keys(
  INVENTORY_INELIGIBILITY_FIELDS,
).reduce(
  (acc, key) => {
    acc[key] = INVENTORY_INELIGIBILITY_FIELDS[key].reason
    return acc
  },
  {} as Record<InventoryFieldKeys, string>,
)

export const INVENTORY_DISTRIBUTION_DB_FIELDS: Record<InventoryFieldKeys, string> = Object.keys(
  INVENTORY_INELIGIBILITY_FIELDS,
).reduce(
  (acc, key) => {
    acc[key] = INVENTORY_INELIGIBILITY_FIELDS[key].dbField
    return acc
  },
  {} as Record<InventoryFieldKeys, string>,
)

export const INVENTORY_DB_FIELDS_TO_LABELS = Object.keys(INVENTORY_INELIGIBILITY_FIELDS).reduce(
  (acc, key) => {
    acc[INVENTORY_INELIGIBILITY_FIELDS[key].dbField] = INVENTORY_INELIGIBILITY_FIELDS[key].reason
    return acc
  },
  {} as Record<string, string>,
)

/**
 * Sentinel buckets for the inventory aging charts. Values are chosen so a plain
 * numeric sort yields the axis order the exhibit needs: Expired, 0-90 … 361+, No Data.
 * Only the expiration chart can produce Expired — stock cannot land in the past.
 */
export const INVENTORY_AGING_EXPIRED_BUCKET = -1
export const INVENTORY_AGING_NO_DATA_BUCKET = 9999

/**
 * The two distribution fields the aging exhibits chart on. Spelled out rather than derived
 * from `InventoryFieldKeys`, which is `string` — `INVENTORY_INELIGIBILITY_FIELDS` is typed as
 * `Record<string, …>`, so narrowing from it yields `never`.
 */
export type InventoryAgingField = 'daysSinceLanded' | 'daysUntilExpiration'

export const INVENTORY_AGING_FIELDS: InventoryAgingField[] = [
  'daysSinceLanded',
  'daysUntilExpiration',
]

/**
 * Every bucket an aging exhibit puts on its axis, in axis order. The set is fixed so the
 * exhibit is comparable across reports: a bucket with nothing in it is a zero bar, not a
 * missing column.
 */
export const getInventoryAgingBuckets = (field: InventoryAgingField): number[] => [
  ...(field === 'daysUntilExpiration' ? [INVENTORY_AGING_EXPIRED_BUCKET] : []),
  0,
  91,
  181,
  271,
  361,
  INVENTORY_AGING_NO_DATA_BUCKET,
]

export const DAYS_LABELS = {
  '-1': 'Expired',
  '0': '0-90 Days',
  '91': '91-180 Days',
  '181': '181-270 Days',
  '271': '271-360 Days',
  '361': '361+ Days',
  '9999': 'No Data',
}

export const INVENTORY_ATTRIBUTE_FIELDS: InventoryFieldKeys[] = [
  'type',
  'season',
  'size',
  'brandCategory',
  'industrySubcategory',
  'flavor',
  'style',
  'color',
  'custom_1',
  'custom_2',
  'custom_3',
  'custom_4',
  'custom_5',
  'department',
  'dwightCategory',
  'dwightSubcategory',
  'dwightSize',
  'dwightDepartment',
]

export const SALES_BY_SKU_ATTRIBUTE_FIELDS = [
  'type',
  'season',
  'size',
  'brand_category',
  'industry_subcategory',
  'flavor',
  'style',
  'color',
  'custom_1',
  'custom_2',
  'custom_3',
  'custom_4',
  'custom_5',
  'department',
  'dwight_category',
  'dwight_subcategory',
  'dwight_size',
  'dwight_department',
]

export interface IInventoryIneligibilityField {
  id: string
  field: InventoryFieldKeys
  ineligibleReason: IneligibleReasons
  clientName: string
  clientInfo?: IClientInfo
}

export enum CUSTOM_RULES_FIELD {
  Location = 'location',
  SKU = 'sku',
  Description = 'description',
  Type = 'type',
  Season = 'season',
  Size = 'size',
  Flavor = 'flavor',
  Style = 'style',
  Color = 'color',
  Category = 'brandCategory',
  'Sub Category' = 'industrySubcategory',
  'Lot Number' = 'lotNumber',
  'On Hand' = 'onHand',
  'Average Unit Cost' = 'avgUnitCost',
  'Total Value' = 'totalValue',
  'Landed Date' = 'landedDate',
  'Expiration Date' = 'expirationDate',
  'Days Since Landed' = 'daysSinceLanded',
  'Days Until Expiration' = 'daysUntilExpiration',
  'Sales Units' = 'salesUnits',
  Sales = 'sales',
  'Weeks Of Supply' = 'weeksOfSupply',
  'Custom 1' = 'custom_1',
  'Custom 2' = 'custom_2',
  'Custom 3' = 'custom_3',
  'Custom 4' = 'custom_4',
  'Custom 5' = 'custom_5',
  Department = 'department',
  'Standard Category' = 'dwightCategory',
  'Standard Subcategory' = 'dwightSubcategory',
  'Standard Size' = 'dwightSize',
  'Standard Department' = 'dwightDepartment',
}

export const INVENTORY_FIELD_TO_LABEL = invert(CUSTOM_RULES_FIELD)

export enum CUSTOM_RULES_OPERATOR {
  '>' = '>',
  '=' = '=',
  '<' = '<',
  contains = 'contains',
}

export const CUSTOM_RULES_FIELD_TYPES: Record<string, string> = {
  recordDate: 'date',
  sku: 'string',
  description: 'string',
  type: 'string',
  location: 'string',
  season: 'string',
  size: 'string',
  lotNumber: 'string',
  onHand: 'number',
  avgUnitCost: 'number',
  totalValue: 'number',
  landedDate: 'date',
  expirationDate: 'date',
  daysSinceLanded: 'number',
  daysUntilExpiration: 'number',
  sales: 'number',
  salesUnits: 'number',
  weeksOfSupply: 'number',
  custom_1: 'string',
  custom_2: 'string',
  custom_3: 'string',
  custom_4: 'string',
  custom_5: 'string',
  flavor: 'string',
  style: 'string',
  color: 'string',
  brandCategory: 'string',
  industrySubcategory: 'string',
  department: 'string',
  dwightCategory: 'string',
  dwightSubcategory: 'string',
  dwightSize: 'string',
  dwightDepartment: 'string',
}

/** `operator` is a free-text column, so it is narrowed back to the enum rather than compared to it. */
export const customRuleOperator = enumNarrow(CUSTOM_RULES_OPERATOR)

/**
 * `contains` is a case-insensitive substring match, so it is offered only on text columns. Its
 * value is the raw comma-separated text the user typed (see `splitCommaSeparated`); the other
 * operators compare the column directly.
 */
const TEXT_CUSTOM_RULES_OPERATORS = [CUSTOM_RULES_OPERATOR['='], CUSTOM_RULES_OPERATOR.contains]

const COMPARISON_CUSTOM_RULES_OPERATORS = [
  CUSTOM_RULES_OPERATOR['>'],
  CUSTOM_RULES_OPERATOR['='],
  CUSTOM_RULES_OPERATOR['<'],
]

export const isTextCustomRuleField = (field?: string): boolean =>
  !!field && CUSTOM_RULES_FIELD_TYPES[field] === 'string'

export const getCustomRuleOperators = (field?: string): CUSTOM_RULES_OPERATOR[] =>
  isTextCustomRuleField(field) ? TEXT_CUSTOM_RULES_OPERATORS : COMPARISON_CUSTOM_RULES_OPERATORS

/**
 * `=` is stored as a list of values (`values`); every other operator stores a single `value`.
 * Dates are the exception — their picker yields one value even under `=`.
 */
export const isMultiValueCustomRule = (field?: string, operator?: string): boolean =>
  customRuleOperator.as(operator) === CUSTOM_RULES_OPERATOR['='] &&
  CUSTOM_RULES_FIELD_TYPES[field || ''] !== 'date'

export interface IInventoryIneligibilityCustomRule {
  id: string
  field: CUSTOM_RULES_FIELD
  label: string
  operator: CUSTOM_RULES_OPERATOR
  value: any
  values: any[]
  eligible: boolean
  overrides: string[]
  clientName: string
  clientInfo?: IClientInfo
  children: IInventoryIneligibilityCustomRule[]
}

export interface IInventoryIneligibilityAnalisysSKU {
  description: string
  sku: string
  totalValue: number
  percent: number
  type: string
  location: string
  weeksOfSupply: number
  eligible: boolean
  totalValueAll: number
  totalItems: number
  totalAvailability: number
}

export interface IInventoryIneligibilityAnalisysSKUData {
  data: IInventoryIneligibilityAnalisysSKU[]
  totals: {
    totalItems: number
    totalValueTop5: number
    totalValueTop5Percent: number
    totalAvailabilityTop5: number
    totalValueTop10: number
    totalValueTop10Percent: number
    totalAvailabilityTop10: number
    totalValueAll: number
    totalAvailabilityAll: number
  }
}

export interface IInventoryDistributionAnalysisData {
  inventoryDates: { id: string; recordDate: string }[]
  data: IInventoryDistributionAnalysis[]
  dateRangeMinMax: { minDate: string; maxDate: string } | null
}

export interface IInventoryDistributionAnalysisCategories {
  filterDropdownValues: {
    [key: string]: string[]
  }
  distributionPicklist: InventoryFieldKeys[]
}

export interface IInventoryDistributionAnalysis {
  distribution: string
  eligible: boolean
  values: {
    [id: string]: {
      value: number
      percent: number
    }
  }
  mappedLocation?: string
}
export interface ISalesDistributionAnalysisData {
  dates: string[]
  data: ISalesDistributionAnalysis[]
}

export interface ISalesDistributionAnalysis {
  distribution: string
  values: {
    [recordDate: string]: {
      value: number
      percentValue: number
      units: number
      percentUnits: number
    }
  }
  totalSales: number
  totalUnits: number
}

/** A customer's ineligible AR by reason, keyed by ArIneligibility column, in the order calculateArIneligibility applies them. */
export const AR_INELIGIBILITY_REASON_LABELS = {
  ineligibleCategoryAmount: 'Ineligible Customer',
  pastDue: 'Past Due',
  crossAge: 'Cross Age',
  ineligibleInvoices: 'Ineligible Invoices',
  contras: 'Contras',
  excessConcentrationLimit: 'Excess Concentration',
  excessOfCreditLimit: 'Excess Credit Limit',
} as const

export type ArIneligibilityReason = keyof typeof AR_INELIGIBILITY_REASON_LABELS

export const AR_INELIGIBILITY_REASONS = Object.keys(
  AR_INELIGIBILITY_REASON_LABELS,
) as ArIneligibilityReason[]

export type IArIneligibleReasons = Partial<Record<ArIneligibilityReason, number>>

export interface IArRawDataItem {
  id?: string
  clientInfo?: IClientInfo
  invoiceDate?: string
  asOfDate?: string
  balance?: number
  debtor?: string
  linkedName?: string
  predictions?: string[]
  invoice?: string
  daysPastInvoice?: number
  memo?: string
  prediction?: string
  totalItems?: number
}

export interface IApRawDataItem {
  id?: string
  clientInfo?: IClientInfo
  invoiceDate?: string
  asOfDate?: string
  balance?: number
  creditor?: string
  linkedName?: string
  predictions?: string[]
  invoice?: string
  daysPastInvoice?: number
  memo?: string
  prediction?: string
  totalItems?: number
}

export interface IAlias {
  id?: string
  originalName?: string
  aka?: string
  linkedName?: string
  prediction?: string
  totalItems?: number
  totalUnmapped?: number
  confidence?: number
  /** Jev's probability (0-1) on the predicted entity; null when an exact match or Claude predicted it. */
  predictionProbability?: number | null
  isNew?: boolean
  lastMappedDate?: string
  clientName?: string
  isNewEntity?: boolean
  entityId?: string
  totalAmount?: number
  belowPredictionThreshold?: number
}

export interface IOveradvanceActivity {
  id?: string
  overadvanceId?: string
  recordDate: string
  activityType?: 'increase' | 'decrease'
  amount?: number
  newBalance?: number
  runningBalance?: number
  description?: string
  boxLink?: string
  createdAt?: string
  updatedAt?: string
}

export interface IOveradvance {
  id: string
  clientName: string
  status: OveradvanceStatus
  clientInfo?: IClientInfo
  // overadvance / startDate are null while an analysis-flow Draft is being
  // filled in — the Finalized/Executed gate guarantees them before the
  // overadvance goes live.
  overadvance: number | null
  startDate: string | null
  endDate: string | null
  fee: number | null
  rate?: number
  floor: number
  marginAbovePrime: number
  purpose?: string
  useTieredRate?: boolean
  useAblRate?: boolean
  currentBalance?: number
  /** Date the balance was repaid to zero after being drawn; null if outstanding or never drawn. */
  payoffDate?: string | null
  createdAt?: string
  overadvanceActivities?: IOveradvanceActivity[]
  summaryOfChanges?: string
  amendments?: IAmendment[]
  /**
   * Set on the list response when this overadvance is Draft — surfaces the
   * status of the analysis that owns it so the UI can decide whether to
   * render the Delete button (allowed only once the analysis is Archived).
   */
  linkedAnalysisStatus?: AmendmentAnalysisStatus | null
  /**
   * Companion to `linkedAnalysisStatus`: the owning analysis's id, so the
   * Draft edit flow can navigate back to the analysis page after saving.
   */
  linkedAnalysisId?: string | null
  openAmendmentAnalysis?: IOpenAmendmentAnalysis | null
}

export type OveradvanceAmendmentField = {
  value: keyof IOveradvance
  label: string
  dbName: string
  type: FieldType
}

export const OVERADVANCE_AMENDMENT_FIELDS: OveradvanceAmendmentField[] = [
  { value: 'endDate', label: 'End Date', dbName: 'end_date', type: FieldType.Date },
  { value: 'floor', label: 'Floor', dbName: 'floor', type: FieldType.Percent },
  {
    value: 'marginAbovePrime',
    label: 'Margin above Prime',
    dbName: 'margin_above_prime',
    type: FieldType.Percent,
  },
  { value: 'purpose', label: 'Purpose', dbName: 'purpose', type: FieldType.Text },
  { value: 'useAblRate', label: 'Use ABL Rate', dbName: 'use_abl_rate', type: FieldType.Boolean },
  {
    value: 'useTieredRate',
    label: 'Use Tiered Rate',
    dbName: 'use_tiered_rate',
    type: FieldType.Boolean,
  },
]

export const STANDARDISED_INVENTORY_DETAIL_DOCUMENT_REQUIRED_COLUMNS = [
  'sku',
  'description',
  'location',
]

export const STANDARDIZED_INVENTORY_DETAIL_DOCUMENT_VALUE_COLUMNS = [
  'onHand',
  'avgUnitCost',
  'totalValue',
]

export const STANDARDISED_MASTER_INVENTORY_OPTIONAL_FIELD_MAPPING_WITH_SPACES = {
  sku: 'sku',
  description: 'description',
  'type (e.g. work in process, finished goods)': 'type',
  category: 'brand_category',
  'collaboration (y/n)': 'collaboration',
  'season (apparel)': 'season',
  'flavor (food & bev)': 'flavor',
  'color (apparel)': 'color',
  'size (e.g., xs, 12oz, 3 pack)': 'size',
  season: 'season',
  size: 'size',
  type: 'type',
  color: 'color',
  flavor: 'flavor',
  style: 'style',
  'sub category': 'industry_subcategory',
  subcategory: 'industry_subcategory',
  'brand category': 'brand_category',
  department: 'department',
  'dwight category': 'dwight_category',
  'dwight subcategory': 'dwight_subcategory',
  'dwight size': 'dwight_size',
  'dwight department': 'dwight_department',
  'standard category': 'dwight_category',
  'standard subcategory': 'dwight_subcategory',
  'standard sub category': 'dwight_subcategory',
  'standard size': 'dwight_size',
  'standard department': 'dwight_department',
}

const filterObjectAndAddUnderscore = (obj: Record<string, string>) =>
  Object.keys(obj).reduce(
    (acc, key) => {
      if (key.includes(' ')) {
        acc[key.replace(/ /g, '_')] = obj[key]
      }
      return acc
    },
    {} as Record<string, string>,
  )

export const STANDARDISED_MASTER_INVENTORY_OPTIONAL_FIELD_MAPPING: Record<string, string> = {
  ...STANDARDISED_MASTER_INVENTORY_OPTIONAL_FIELD_MAPPING_WITH_SPACES,
  ...filterObjectAndAddUnderscore(STANDARDISED_MASTER_INVENTORY_OPTIONAL_FIELD_MAPPING_WITH_SPACES),
}
const STANDARDISED_MASTER_INVENTORY_OPTIONAL_FIELD_MAPPING_WITH_SPACES_KEYS = {
  location: 'location',
  'on hand': 'onHand',
  'avg unit cost': 'avgUnitCost',
  'landed date': 'landedDate',
  'expiration date (food & bev, beauty)': 'expirationDate',
  'custom 1': 'custom_1',
  'custom 2': 'custom_2',
  'custom 3': 'custom_3',
  'custom 4': 'custom_4',
  'custom 5': 'custom_5',
  'lot number': 'lotNumber',
  'expiration date': 'expirationDate',
  'total value': 'totalValue',
}

// How CLIENTS phrase an inventory header ('total value', 'landed date'), mapped
// to the canonical field key.
const STANDARDISED_INVENTORY_DETAIL_DOCUMENT_CLIENT_PHRASINGS: Record<string, string> = {
  ...STANDARDISED_MASTER_INVENTORY_OPTIONAL_FIELD_MAPPING_WITH_SPACES_KEYS,
  ...filterObjectAndAddUnderscore(
    STANDARDISED_MASTER_INVENTORY_OPTIONAL_FIELD_MAPPING_WITH_SPACES_KEYS,
  ),
  ...Object.keys(STANDARDISED_MASTER_INVENTORY_OPTIONAL_FIELD_MAPPING).reduce(
    (acc, key) => {
      acc[key] = STANDARDISED_MASTER_INVENTORY_OPTIONAL_FIELD_MAPPING[key]
        .toLowerCase()
        .replace(/[-_][a-z]/g, (group: string) => group.slice(-1).toUpperCase())
      return acc
    },
    {} as Record<string, string>,
  ),
}

/**
 * Every canonical field key, keyed to itself.
 *
 * The phrasings above are the ones clients write, which is the right vocabulary
 * for a client's own export and the wrong one for a file WE wrote. The
 * preprocessing agent emits its cleaned sheet with these canonical keys as the
 * header row — that is the vocabulary we ask it to map into — and 'totalValue',
 * 'avgUnitCost' and 'landedDate' appear nowhere above. So a rewrite produced in
 * our own words came back unreadable: the value column was dropped on the way
 * in, and the reconciliation meant to prove the rewrite good instead reported
 * that it had no value column at all, on a sheet that tied out to the cent.
 *
 * Derived from the values rather than listed, so a canonical field cannot be
 * added without its own key resolving. Spread FIRST so a client phrasing that
 * happens to collide always wins.
 */
const keyedToThemselves = (mapping: Record<string, string>): Record<string, string> =>
  Object.values(mapping).reduce<Record<string, string>>((acc, canonical) => {
    acc[canonical.toLowerCase()] = canonical
    return acc
  }, {})

export const STANDARDISED_INVENTORY_DETAIL_DOCUMENT_FIELD_MAPPING: Record<string, string> = {
  ...keyedToThemselves(STANDARDISED_INVENTORY_DETAIL_DOCUMENT_CLIENT_PHRASINGS),
  ...STANDARDISED_INVENTORY_DETAIL_DOCUMENT_CLIENT_PHRASINGS,
}

export const STANDARDISED_SALES_BY_SKU_REQUIRED_COLUMNS = [
  'sku',
  'record_date',
  'last_6_month_sales_units',
  'last_6_month_sales_amount',
]

export const STANDARDIZED_ATTRIBUTE_COLUMNS = [
  'dwight_category',
  'dwight_subcategory',
  'dwight_size',
  'dwight_department',
]

export const STANDARDISED_SALES_BY_SKU_FIELD_MAPPING: Record<string, string> = {
  sku: 'sku',
  record_date: 'recordDate',
  description: 'description',
  last_6_month_sales_units: 'l6mSalesUnits',
  last_6_month_sales_amount: 'l6mSalesAmount',
}

export const STANDARDISED_SALES_BY_SKU_DETAIL_REQUIRED_COLUMNS = [
  'sku',
  'record_date',
  'period',
  'sales_units',
  'sales_amount',
]

export const SALES_BY_SKU_DETAIL_NON_MONTHLY_COLUMNS: Record<string, string> = {
  sku: 'sku',
  record_date: 'record_date',
  'record date': 'record_date',
  period: 'period',
  sales_units: 'sales_units',
  'sales units': 'sales_units',
  'sales amount': 'sales_amount',
  sales_amount: 'sales_amount',
}

export const STANDARDISED_SALES_BY_SKU_DETAIL_FIELD_MAPPING: Record<string, string> = {
  sku: 'sku',
  description: 'description',
  period: 'period',
  record_date: 'recordDate',
  recorddate: 'recordDate',
  sales_units: 'salesUnits',
  salesunits: 'salesUnits',
  sales_amount: 'salesAmount',
  salesamount: 'salesAmount',
}

export interface IInventoryMappingFields {
  id: string
  sku: string
  description: string
  brandCategory: string
  industrySubcategory: string
  season: string
  type: string
  size: string
  flavor: string
  style: string
  color: string
  eligible: boolean
  notes?: string
}

export interface IInventoryMappingFieldsData {
  data: IInventoryMappingFields[]
  totalItems: number
}

export interface ISalesBySKUVisualizationTotals {
  top5: ISalesBySKUVisualization
  top10: ISalesBySKUVisualization
  all: ISalesBySKUVisualization
}

export interface ISalesBySKUVisualizationData {
  data: ISalesBySKUVisualization[]
  totalItems: number
  totalDuplicateSkus: number
  totalUniqueDuplicateSkus: number
  totals: ISalesBySKUVisualizationTotals
  dates: string[]
}

export interface ISalesBySKUIneligibleReason {
  reason: string | null
  amount: number
}

export interface ISalesBySKUVisualization {
  distribution: string
  sku: string
  onHand: number
  totalValue: number
  totalInventoryPercent: number
  unitSales: number
  totalSales: number
  totalSalesPercent: number
  totalCOGS: number
  weeklyCOGS: number
  weeksOfSupply: number
  totalIneligible: number
  /** Total Ineligible split by first ineligible reason, labelled as in the Ineligible Reasons view. */
  ineligibleReasons?: ISalesBySKUIneligibleReason[] | null
  monthsOfSales: number
  monthsOfSalesCount: number
  totalSalesValue: number
  totalInventoryValue: number
  totalItems: number
  previousOnHandDifference: number
  previousTotalValueDifference: number
  isNew?: boolean
  unitSalesPerMonth?: number[]
  totalSalesPerMonth?: number[]
}

export interface ISalesBySKUVisualizationFilters {
  filterDropdownValues: {
    [key: string]: string[]
  }
  distributionPicklist: string[]
  inventoryDates: string[]
  latestSalesDate: string
  earliestSalesDate: string
}

export interface IARRollforwardDetail {
  debtor: string
  invoice: string
  newInvoices: number
  impliedCollections: number
  actualCollections: number
  impliedReal: number
  newCredits: number
  creditsApplied: number
  totalCount: number
  invoiceDate: string
  lastAppearedDate?: string // For sorting (YYYY-MM-DD)
  paidDate: string // For display (formatted as "YYYY-MM-DD - YYYY-MM-DD" or "YYYY-MM-DD")
  isPartiallyPaid?: boolean
}

export interface IAPRollforwardDetail {
  invoice: string
  newBills: number
  impliedPayments: number
  newCredits: number
  creditsApplied: number
  totalCount: number
  invoiceDate: string
  paidDate: string
}

export interface IARRollforwardSummary {
  entityId: string
  label: string
  invoiceCount?: number
  newBalance: number
  percent: number
  newInvoices: number
  impliedCollections: number
  actualCollections: number
  impliedReal: number
  impliedDilution: number
  newCredits: number
  creditsApplied: number
  rows?: IARRollforwardDetail[]
}

export interface IARRollforwardGraphDataItem {
  recordDate: string
  header: string
  newInvoices: number
  actualCollections: number
  impliedCollections: number
  impliedDilution: number
}

export interface IARRollforwardGraphData {
  headers: string[]
  data: IARRollforwardGraphDataItem[]
  minDate: string | null
  maxDate: string | null
  debtors: string[]
}

export interface IAPRollforwardSummary {
  entityId: string
  priorityPayable: number
  label: string
  invoiceCount?: number
  newBalance: number
  percent: number
  newBills: number
  impliedPayments: number
  newCredits: number
  creditsApplied: number
  rows?: IAPRollforwardDetail[]
}

export interface IArRollforwardData {
  currentFunding?: IBorrowingBase
  previousFunding?: IBorrowingBase
  data: IARRollforwardSummary[]
  totalItems: number
  totals: IARRollforwardSummary[]
  ineligibleCategories: string[]
}

export interface IApRollforwardData {
  currentFunding?: IBorrowingBase
  previousFunding?: IBorrowingBase
  data: IAPRollforwardSummary[]
  totalItems: number
  totals: IAPRollforwardSummary[]
}

export enum ReserveKind {
  Absolute = 'absolute',
  Percent = 'percent',
}

export enum APExtraReserveType {
  AP = 'AP',
}

export enum APExtraReserveCategory {
  PriorityCustom = 'ap_priority_custom',
  PriorityOverride = 'ap_priority_override',
}

export interface IReserve {
  id: string
  clientName: string
  type: CollateralTypes
  category: string
  amount: number
  percent?: number
  reason: string | null
  kind: ReserveKind
  field: string | null
}

export interface IInventoryIneligibleStats {
  data: {
    category: string
    values: { [key: string]: number }
    rows: IInventoryIneligibleStatsReason[]
  }[]
  total: number
  dates: string[]
  totalIneligible?: number[]
  totalEligible?: number[]
}

export interface IInventoryIneligibleStatsReason {
  category: string
  reason: string
  values: { [key: string]: number }
  totalCount: number
  rows: IInventoryIneligibleStatsDetail[]
}

export interface IInventoryIneligibleStatsDetail {
  sku: string
  description: string
  category: string
  reason: string
  values: { [key: string]: number }
  totalCount: number
}

export interface IARIneligibleStatsDetailsInvoice {
  masterInvoiceId?: string
  invoice: string
  /** The customer (debtor) the invoice belongs to; absent when no alias resolves it. */
  debtor?: string
  /** Set only when the debtor maps to an entity — drives the link to the Entity page. */
  entityId?: string
  totalCount: number
  value_0: number
  value_1?: number
  value_2?: number
  value_3?: number
  value_4?: number
}

export interface IARIneligibleStatsDetails {
  debtor: string
  totalCount: number
  value_0: number
  value_1?: number
  value_2?: number
  value_3?: number
  value_4?: number
  rows: IARIneligibleStatsDetailsInvoice[]
}

export interface IARIneligibleStats {
  label: string
  values: number[]
  rows: IARIneligibleStatsDetails[]
}

export const QUICKBOOK_FIELDS = {
  '!trns': '!TRNS',
  trnstype: 'TRNSTYPE',
  date: 'DATE',
  accnt: 'ACCNT',
  name: 'NAME',
  amount: 'AMOUNT',
  docnum: 'DOCNUM',
  memo: 'MEMO',
}

export interface QuickbookReferenceData {
  '!TRNS': string
  TRNSTYPE: string
  DATE: string
  ACCNT: string
  NAME: string
  AMOUNT: number
  DOCNUM: string
  MEMO: string
}

interface IArSummaryStatsBase {
  debtor: string
  invoice: string | null
  daysPastInvoice: number | null
  ar_1To_30Days: number
  ar_31To_60Days: number
  ar_61To_90Days: number
  ar_91PlusDays: number
  ineligible: number
  eligible: number
  total: number
  percent: number
  totalCount?: number
}

export interface IArSummaryStats extends IArSummaryStatsBase {
  entityId: string
  debtor: string
  memo: string
  // Present on invoice-level rows that came from a QuickBooks export.
  qboTxnId?: string | null
  transactionType?: string | null
  ar_1To_30DaysChanged?: number
  ar_31To_60DaysChanged?: number
  ar_61To_90DaysChanged?: number
  ar_91PlusDaysChanged?: number
  ineligibleChanged?: number
  eligibleChanged: number
  totalChanged?: number
  invoiceCount?: number
  invoiceCountChanged?: number
  daysPastInvoiceChanged?: number
  ineligiblePerDebtor: number
  /** Only reasons with an amount; they sum to `ineligible`. */
  ineligibleReasons?: IArIneligibleReasons
  total: number
  percent: number
  percentChanged: number
  category: Category | null
  isEligible: boolean
  rows?: IArSummaryStats[]
  entityWebsite?: string
  entityName?: string
  entityIconUrl?: string
  entityInfo?: { id: string; name: string; website?: string }
}

interface IArSummaryStatsTotals {
  ar_1To_30Days: number
  ar_1To_30DaysPercent: number
  ar_31To_60Days: number
  ar_31To_60DaysPercent: number
  ar_61To_90Days: number
  ar_61To_90DaysPercent: number
  ar_91PlusDays: number
  ar_91PlusDaysPercent: number
  ineligible: number
  eligible: number
  ineligiblePercent: number
  eligiblePercent: number
  total: number
}

export interface IArSummaryStatsData {
  data: IArSummaryStats[]
  totals: IArSummaryStatsTotals
}

interface IApSummaryStatsBase {
  memo: string
  invoice: string | null
  daysPastInvoice: number | null
  ap_1To_30Days: number
  ap_31To_60Days: number
  ap_61To_90Days: number
  ap_91PlusDays: number
  total: number
  percent: number
  totalCount?: number
}

export interface IApSummaryStats extends IApSummaryStatsBase {
  entityId: string
  // Present on bill-level rows that came from a QuickBooks export.
  qboTxnId?: string | null
  transactionType?: string | null
  entityWebsite?: string
  entityName?: string
  entityIconUrl?: string
  entityInfo?: { id: string; name: string; website?: string }
  creditor: string
  priorityPayable: number | null
  ap_1To_30DaysChanged: number
  ap_31To_60DaysChanged: number
  ap_61To_90DaysChanged: number
  ap_91PlusDaysChanged: number
  invoiceCount?: number
  invoiceCountChanged?: number
  daysPastInvoiceChanged?: number
  percentChanged: number
  totalChanged: number
  rows?: IApSummaryStats[]
}
interface IApSummaryStatsTotals {
  ap_1To_30Days: number
  ap_1To_30DaysPercent: number
  ap_31To_60Days: number
  ap_31To_60DaysPercent: number
  ap_61To_90Days: number
  ap_61To_90DaysPercent: number
  ap_91PlusDays: number
  ap_91PlusDaysPercent: number
  total: number
}

export interface IApSummaryStatsData {
  data: IApSummaryStats[]
  totals: IApSummaryStatsTotals
}

export interface IFlags {
  id?: string
  flagName?: string
  flagTitle?: string | null
  flagType?: FlagTypes
  flagLod?: string
  message?: string
  impactToAvailability?: number | null
  sourceId?: string | null
  masterInvoiceId?: string | null
  resolved?: boolean
  feedback?: Feedback
  feedbacks?: IFlagFeedback[]
  invoice?: string
  invoiceCount?: number
  debtor?: string
  notes?: INote
  borrowingBase?: Pick<IBorrowingBase, 'id' | 'recordDate' | 'isTest'>
  clientInfo?: Pick<IClientInfo, 'id' | 'clientName'>
  ongoingReporting?: Pick<IOngoingReporting, 'id' | 'recordDate' | 'type'>
  ongoingReportingId?: string
  opsReporting?: Pick<IOPSReporting, 'id' | 'recordDate'>
  flagColor: FlagColors | null
  masterFlag?: Pick<
    IMasterFlag,
    | 'id'
    | 'flagName'
    | 'tooltipMessage'
    | 'yellowThreshold'
    | 'redThreshold'
    | 'alwaysYellow'
    | 'alwaysRed'
  >
  entityInfo?: Pick<IEntityInfo, 'id' | 'name'>
  recordDate?: string
}

export interface IFlagData {
  data: IFlags[]
  totalItems: number
  newFlagCount: number // need to setup
}

export interface IFlagsOptions {
  data: {
    flagNames: { value: string; label: string }[]
  }
}

export interface IMasterFlag {
  id: string
  flagName: string
  tooltipMessage: string
  yellowThreshold: number | null
  redThreshold: number | null
  alwaysYellow: boolean
  alwaysRed: boolean
}

export interface IInventoryDataItem {
  id?: string
  sku?: string
  description?: string
  type?: string
  location?: string
  onHand?: number
  avgUnitCost?: number
  totalValue?: number
  recordDate?: string
}

export interface IBBCFlagDetails extends IFlags {
  clientArRawCurrent?: IArRawDataItem
  clientArRawPrevious?: IArRawDataItem
  clientInventoryCurrent?: IInventoryDataItem
  clientInventoryPrevious?: IInventoryDataItem
}

export interface IBBCFlagDetailsData {
  data?: IBBCFlagDetails[]
  totalItems?: number
}

export enum FlagTypes {
  BBC = 'BBC',
  Financials = 'Financials',
  GL = 'GL',
  OPS = 'Ops',
  Entity = 'Entity',
  Client = 'Client',
}

export enum FlagStatuses {
  Active = 'ACTIVE',
  Inactive = 'INACTIVE',
}

export enum FlagLODs {
  Detail = 'Detail',
  Summary = 'Summary',
}

export enum FlagColors {
  Yellow = 'yellow',
  Red = 'red',
}

export const FLAG_TYPE_LABELS: Partial<Record<FlagTypes, string>> = {
  [FlagTypes.Client]: 'Client',
  [FlagTypes.BBC]: 'Borrowing Base',
  [FlagTypes.Financials]: 'Financials',
  [FlagTypes.GL]: 'General Ledger',
  [FlagTypes.OPS]: 'OPS',
  [FlagTypes.Entity]: 'Entity',
}

export interface IBbcFlagType {
  tablePrevious: string
  tableCurrent: string
  labelDbField: string
  referenceDbField: string
  dateField: string
}

export const BBC_FLAG_TYPES: Record<string, IBbcFlagType> = {
  ar: {
    tablePrevious: 'clientArRawPrevious',
    tableCurrent: 'clientArRawCurrent',
    labelDbField: 'invoice',
    referenceDbField: 'memo',
    dateField: 'asOfDate',
  },
  inventory: {
    tablePrevious: 'clientInventoryPrevious',
    tableCurrent: 'clientInventoryCurrent',
    labelDbField: 'description',
    referenceDbField: 'sku',
    dateField: 'recordDate',
  },
}

type DetailedBbcFlagsMapping = {
  field?: string
  dbField?: string
  dateField?: string
  label?: string
  type?: FilterFieldType | string
  sourceData?: string
  showMessage?: boolean
  hash?: string
  [extra: string]: unknown
}

export const DETAILED_BBC_FLAGS_MAPPING: Record<string, DetailedBbcFlagsMapping> = {
  altered_date_flag: {
    field: 'invoiceDate',
    dbField: 'invoice_date',
    dateField: 'as_of_date',
    label: 'Invoice Date',
    type: FilterFieldType.Date,
    sourceData: 'ar',
    showMessage: true,
  },
  altered_amount_flag: {
    field: 'balance',
    dbField: 'balance',
    dateField: 'as_of_date',
    label: 'Balance',
    type: FilterFieldType.Amount,
    sourceData: 'ar',
  },
  duplicate_ar_invoices: {
    field: 'invoice',
    dbField: 'invoice',
    dateField: 'as_of_date',
    label: 'invoice',
    type: FilterFieldType.Number,
    sourceData: 'ar',
  },
  reappearing_invoice: {
    field: 'balance',
    dbField: 'balance',
    dateField: 'as_of_date',
    label: 'Balance',
    type: FilterFieldType.Number,
    sourceData: 'ar',
  },
  priority_invoice_paid: {
    field: 'invoice',
    dbField: 'invoice',
    dateField: 'as_of_date',
    label: 'invoice',
    type: FilterFieldType.Number,
    sourceData: 'ap',
  },
  paid_invoice: {
    field: 'paidAmount',
    dbField: 'paid_amount',
    dateField: 'paid_date',
    label: 'Paid Amount',
    type: FilterFieldType.Amount,
    sourceData: 'masterInvoiceActivity',
  },
  increased_unit_cost: {
    field: 'avgUnitCost',
    dbField: 'avg_unit_cost',
    dateField: 'record_date',
    label: 'Avg Unit Cost',
    type: FilterFieldType.Amount,
    sourceData: 'inventory',
  },
  inventory_build: {
    field: 'onHand',
    dbField: 'on_hand',
    dateField: 'record_date',
    label: 'On Hand',
    type: FilterFieldType.Number,
    sourceData: 'inventory',
  },
  low_current_receivables: {
    hash: '#ar-summary',
    type: 'ar',
  },
  increase_in_near_aged_ar: {
    hash: '#ar-summary',
    type: 'ar',
  },
  increase_in_ineligible: {
    hash: '#ineligible-ar',
    type: 'ar',
  },
  upcoming_3pl_reserves: {
    hash: '#ap-summary',
    type: 'ap',
  },
  potential_liquidity_issue_from_61_90: {
    hash: '#ap-summary',
    type: 'ap',
  },
  potential_liquidity_issue_from_91_plus: {
    hash: '#ap-summary',
    type: 'ap',
  },
  inventory_nearing_exclusion: {
    hash: '#inventory-charts',
    type: 'inventory',
  },
  increase_in_excluded: {
    hash: '#sku-performance',
    type: 'inventory',
  },
  projections_exceed_eligibility_rule: {
    hash: '#',
    type: 'inventory',
  },
  new_skus_flag: {
    hash: '#',
    type: 'inventory-ineligibility',
  },
}

export enum BBC_TABS {
  HISTORICAL = 'Historical',
  RECEIVABLES = 'Receivables',
  PAYABLES = 'Payables',
  INVENTORY = 'Inventory',
}
export enum BBC_AGING_REPORT {
  AR = 'ar',
  AP = 'ap',
}

export interface IARAPChartData {
  '0to30Days': number
  '0to30DaysPercent': number
  '31to60Days': number
  '31to60DaysPercent': number
  '61to90Days': number
  '61to90DaysPercent': number
  '91plusDays': number
  '91plusDaysPercent': number
  total: number
  recordDate: string
}

export interface IARAPChart {
  data: IARAPChartData[]
  entities: string[]
  id: string
}

export interface IARAPChartDropdownOptions {
  entities: string[]
  agingReport: BBC_AGING_REPORT
}

export enum BBC_MAPPING_TABS {
  Customer = 'Customer',
  Vendor = 'Vendor',
}

export const BBC_TAB_FILE_TYPE_MAPPING: Partial<Record<BBC_TABS, PreProcessingSection>> = {
  [BBC_TABS.RECEIVABLES]: OngoingReportingType.AR,
  [BBC_TABS.PAYABLES]: OngoingReportingType.AP,
  [BBC_TABS.INVENTORY]: OngoingReportingType.Inventory,
}

/** The collateral sections pre-processing reads, in the order it reads them. */
export const PRE_PROCESSING_SECTIONS = [
  OngoingReportingType.AR,
  OngoingReportingType.AP,
  OngoingReportingType.Inventory,
] as const

export type PreProcessingSection = (typeof PRE_PROCESSING_SECTIONS)[number]

/** Operator-facing name for a section — the BBC tab its data lands on. */
export const PRE_PROCESSING_SECTION_LABEL: Record<PreProcessingSection, string> = {
  [OngoingReportingType.AR]: BBC_TABS.RECEIVABLES,
  [OngoingReportingType.AP]: BBC_TABS.PAYABLES,
  [OngoingReportingType.Inventory]: BBC_TABS.INVENTORY,
}

type WithReadySections = Pick<IBorrowingBase, 'preProcessingReadySections'>

/**
 * The sections a BBC on this loan type is expected to end up with.
 *
 * Mirrors the summary page's own tabs, and that is the point rather than a
 * coincidence: an outstanding section is only ever cleared by an operator
 * supplying its file from that tab, so counting a section the client's loan type
 * does not have would freeze the BBC's availability with nowhere to go and fix it.
 * An AR-only client has no Inventory tab at all.
 *
 * An unknown loan type keeps all three. A freeze an operator can escalate is the
 * better failure: the alternative is recalculating against collateral nobody read,
 * which is the bug this whole mechanism exists to prevent.
 */
export const preProcessingSectionsForLoanType = (
  loanType?: string | null,
): PreProcessingSection[] => {
  if (loanType === LOAN_TYPES.ar) {
    return [OngoingReportingType.AR, OngoingReportingType.AP]
  }
  if (loanType === LOAN_TYPES.inventory) {
    return [OngoingReportingType.AP, OngoingReportingType.Inventory]
  }
  return [...PRE_PROCESSING_SECTIONS]
}

/**
 * This loan type's sections that have (`isReady`) or have not landed on this BBC.
 *
 * NULL and `[]` mean different things and the difference is the whole point.
 * NULL is "this BBC was never tracked per section" — a run that predates the
 * tracking, or a row no run ever touched — so both sides are empty: nothing is
 * known to be missing and nothing is known to have landed, which is how every BBC
 * behaved before. `[]` is "tracking is on and nothing has landed yet", written when
 * a run starts, when one is skipped behind another BBC, and by Force Enter, so an
 * abandoned run cannot be mistaken for a finished one.
 */
const filterPreProcessingSections = (
  borrowingBase: WithReadySections | null | undefined,
  loanType: string | null | undefined,
  isReady: boolean,
): PreProcessingSection[] => {
  const ready = borrowingBase?.preProcessingReadySections
  if (!ready) return []
  return preProcessingSectionsForLoanType(loanType).filter(
    (section) => ready.includes(section) === isReady,
  )
}

/**
 * Sections pre-processing still owes this BBC. Empty is the gate on recalculating
 * availability: a section that has not loaded reads as zero collateral, so
 * recalculating would save figures that are too low over ones that were right.
 */
export const getOutstandingPreProcessingSections = (
  borrowingBase?: WithReadySections | null,
  loanType?: string | null,
): PreProcessingSection[] => filterPreProcessingSections(borrowingBase, loanType, false)

/** Sections an operator can already work on. */
export const getReadyPreProcessingSections = (
  borrowingBase?: WithReadySections | null,
  loanType?: string | null,
): PreProcessingSection[] => filterPreProcessingSections(borrowingBase, loanType, true)

export const formatPreProcessingSections = (sections: PreProcessingSection[]): string =>
  sections.map((section) => PRE_PROCESSING_SECTION_LABEL[section]).join(', ')

export enum ReportingFlow {
  OPS = 'OPS',
  DueDiligence = 'DueDiligence',
  OngoingReporting = 'ongoingReporting',
  ClientPage = 'client',
  ClientUserPage = 'clientUser',
  LoanCommitteeReport = 'loanCommitteeReport',
  BorrowingBase = 'borrowingBase',
  Wires = 'wires',
}

export interface IInventoryLocation {
  id: string
  location: string
  mappedLocation?: string
  entityName?: string
  entity?: IEntityInfo
  warehouseWaiverLink?: string
}

export const MOMENT_FORMAT_BY_PERIOD: Record<ReportingPeriods, string> = {
  [ReportingPeriods.Daily]: 'MMM D, YYYY',
  [ReportingPeriods.Weekly]: 'MMM D, YYYY',
  [ReportingPeriods.Monthly]: 'MMM YYYY',
  [ReportingPeriods.Quarterly]: '[Q]Q YYYY',
  [ReportingPeriods.Annually]: 'YYYY',
}

export const MOMENT_UNIT_OF_TIME_BY_REPORTING_PERIOD: Record<ReportingPeriods, string> = {
  [ReportingPeriods.Daily]: 'day',
  [ReportingPeriods.Weekly]: 'week',
  [ReportingPeriods.Monthly]: 'month',
  [ReportingPeriods.Quarterly]: 'quarter',
  [ReportingPeriods.Annually]: 'year',
}

export const MOMENT_UNIT_OF_TIME_BY_DISPLAY_GROWTH: Record<DisplayGrowthPeriods, string> = {
  [DisplayGrowthPeriods.Hide]: 'month',
  [DisplayGrowthPeriods.Month]: 'month',
  [DisplayGrowthPeriods.Quarter]: 'quarter',
  [DisplayGrowthPeriods.Year]: 'year',
  [DisplayGrowthPeriods.ActualsProjections]: 'month',
  [DisplayGrowthPeriods.StressBase]: 'month',
}

export enum WorkflowPage {
  client = 'client',
  ongoingReporting = 'ongoingReporting',
  borrowingbase = 'borrowingBase',
  dueDilligence = 'dueDilligence',
  opsReporting = 'opsReporting',
}

export const NOT_MAPPED = 'Not Mapped'

export enum BBCReceivablesGroupBy {
  Customer = 'Customer',
  Parent = 'Parent',
}

export const BBC_RECEIVABLES_GROUP_BY_OPTIONS = [
  {
    value: BBCReceivablesGroupBy.Customer,
    label: 'Customer',
  },
  {
    value: BBCReceivablesGroupBy.Parent,
    label: 'Parent entity',
  },
]

export interface IDebtorCategories {
  categories: string[]
}

export const INVENTORY_TYPE_ALIASES: Record<SkuTypes, string[]> = {
  [SkuTypes.Finished]: [
    SkuTypes.Finished.toLowerCase(),
    'finished good',
    'fg',
    'fgi',
    'finished',
    'inventory - product',
    'fg - finished goods',
  ],
  [SkuTypes.InProcess]: [SkuTypes.InProcess.toLowerCase(), 'wip', 'work in progress'],
  [SkuTypes.Raw]: [
    SkuTypes.Raw.toLowerCase(),
    'raw material',
    'rm',
    'raw',
    'inventory - raw materials',
  ],
  [SkuTypes.Packaging]: [
    SkuTypes.Packaging.toLowerCase(),
    'pkg',
    'inventory - packaging inventory',
  ],
  [SkuTypes.Other]: [SkuTypes.Other.toLowerCase(), 'other'],
}

/** Resolve a raw type cell to one of our classes, or null when it is not ours. */
export const resolveInventoryType = (value: unknown): SkuTypes | null => {
  const text = String(value ?? '')
    .trim()
    .toLowerCase()
  if (!text) return null

  return (
    (Object.keys(INVENTORY_TYPE_ALIASES) as SkuTypes[]).find((type) =>
      INVENTORY_TYPE_ALIASES[type].includes(text),
    ) ?? null
  )
}

/**
 * The types the suggestion pass is allowed to propose.
 *
 * `Other/Unspecified` is excluded deliberately: it carries no information ops doesn't already have
 * from the SKU being unclassified, and accepting it would swap an honest blank for a classification
 * that looks deliberate. A SKU the pass can't place is left with no suggestion.
 */
export const PREDICTABLE_SKU_TYPES = [
  SkuTypes.Finished,
  SkuTypes.InProcess,
  SkuTypes.Raw,
  SkuTypes.Packaging,
] as const

export type PredictableSkuType = (typeof PREDICTABLE_SKU_TYPES)[number]

/**
 * What a suggestion was based on. Surfaced to ops on the chip, because a type suggestion that
 * can't be judged is one that just gets accepted.
 */
export interface IInventoryTypePredictionMetadata {
  /** One sentence, composed from the model's distribution and the signals it was shown. */
  reason: string
  model?: string
  predictedAt?: string
}

/**
 * Whether a type column's values are OURS, not just its header.
 *
 * A client's "Inventory Type" column holding "backstock" is a 3PL field that
 * happens to share our word. Every distinct value must resolve — inventory
 * ineligibility is keyed on type, so a partially-read column moves stock in or
 * out of the borrowing base with nothing to show ops it happened.
 */
export const conformsToInventoryTypes = (values: unknown[]): boolean => {
  const present = values.filter((value) => String(value ?? '').trim() !== '')
  if (!present.length) return false

  return present.every((value) => resolveInventoryType(value) !== null)
}

export enum SalesBySKUVisualizationSalesView {
  Aggregate = 'Aggregate',
  Monthly = 'Monthly',
}

export const SALES_BY_SKU_VISUALIZATION_SALES_VIEW_OPTIONS = Object.values(
  SalesBySKUVisualizationSalesView,
).map((value) => ({
  value,
  label: value,
}))

export type ArEligibilityRow = {
  Debtor: string
  Eligible: string | boolean
  Category: Category
  'Credit Limits': string | number
  'Concentration Limit': string | number
  Notes: string | number
}

export const AR_ELIGIBILITY_HEADERS: (keyof ArEligibilityRow)[] = [
  'Debtor',
  'Eligible',
  'Category',
  'Credit Limits',
  'Concentration Limit',
  'Notes',
]

// Master Invoices
export interface IMasterInvoice {
  id: string
  clientName: string
  clientId?: string
  clientIconUrl?: string
  debtor: string
  entityInfo?: {
    id: string
    name: string
  } | null
  invoiceNumber: string
  invoiceDate: string | null
  dueDate: string | null
  // Set when a BBC carried the invoice from a QuickBooks export.
  qboTxnId?: string | null
  qboTxnType?: string | null
  firstAppearedDate: string | null
  lastAppearedDate: string | null
  originalAmount: number
  currentBalance: number
  impliedBalance?: number // Latest balance from BBC activity (ignores wires)
  wireBalance?: number // Latest balance from wire activity (ignores BBCs)
  eligible: boolean
  notes: string | null
  firstBbcId: string | null
  lastBbcId: string | null
  fileId: string | null
  paymentStatus: PaymentStatus
  paidDates: string[] // Array of all paid dates from master_invoice_activity (ordered DESC)
  paidDate?: string // Latest paid date from master_invoice_activity
  reappearedAfterPayment: boolean
  createdAt: string
  updatedAt: string
  // Computed fields from bbc history
  bbcCount?: number
  // Raw fields from query (for backend use)
  debtorEntityId?: string
  debtorEntityName?: string
  // Active flags associated with this invoice
  flags?: Array<{
    id: string
    flagName: string
    flagTitle: string
    message: string
    impactToAvailability: string
  }>
}

export interface IMasterInvoiceBbcHistory {
  id: string
  masterInvoiceId: string
  borrowingBaseId: string
  balanceAtBbc: number
  recordDate: string
  invoiceDate: string
  wasEligible: boolean
  createdAt: string
}

export interface IMasterInvoicesData {
  data: IMasterInvoice[]
  totals: {
    totalItems: number
    totalEligible: number
    totalIneligible: number
    totalPaid: number
  }
  stats?: {
    totalOriginalAmount: number
    totalCurrentBalance: number
  }
}

export interface IMasterInvoiceBbcHistoryData {
  data: IMasterInvoiceBbcHistory[]
  totals: {
    totalItems: number
  }
}

export interface IMasterInvoicePaymentActivity {
  id: string
  recordDate: string
  amount: number
  previousBalance: number
  balance: number
  paymentStatus: PaymentStatus
  wireId: string | null
  wireAddendumId: string | null
  checkRemittanceLineId: string | null
}
